import capitalize from 'lodash/capitalize';
import camelCase from 'lodash/camelCase';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import last from 'lodash/last';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import {AttrsSchema, Component} from 'panel';
import {VNode} from 'snabbdom';

import {prompt} from 'common/components/confirm';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {
  ALL_MATH_TYPES,
  FullPropertyType,
  MATH_TYPE,
  PRIMITIVE_MATH_TYPES,
  ProjectFeatureFlag,
  PropertyType,
  ResourceType,
  SPECIAL_EVENTS,
  XAU_MATH_TYPES,
  XAU_ONLY_MATH_TYPES,
} from 'common/report/constants';
import {QueryContextManager} from 'common/query-context';
import {IronContextRegistry} from 'common/report/context-registry';
import {currentProjectStore} from 'common/report/stores/project-details';
import {dataGroupsClient} from 'common/report/queries/app-api/data-groups-client';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {maxQueryReason} from 'common/report/util/query-tracking';
import {defaultRollingWindowSize, getAlphaLabelFromIndex, RollingWindowSize, serializeAttributes} from 'common/util';
import {insertAtIndex, removeByIndex, replaceByIndex} from 'common/util/array';
import {emitTrackEvent, eventPathContainsSelectors, preventTracking, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {assertUnreachable} from 'common/util/typescript';
import {DEFAULT_DATA_GROUP} from 'common/widgets/data-group-selector';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeFactory} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DisplayContext, TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {NumberOperator, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {TRACKING_EVENTS as QueryBlockTrackingEvent} from 'common/query-builder/query-block/constants';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {filterToBookmarkableEventItem} from 'common/query-builder/query-block/events-menu/util';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {
  isSamePropertyReference,
  segmentationToPropertyItem,
} from 'common/query-builder/query-block/properties-menu/util';
import {
  hasInlineBehaviors,
  isSupportedMultiAttributionTypeForEntry,
  isXauMathType,
  qbStyleAttrs,
  queryBuilderUtils,
} from 'common/query-builder/query-builder/util';
import {RangeSelection} from 'common/widgets/step-range-selector/types';
import {isBehavioralCustomProperty} from 'common/widgets/custom-property-modal/util';
import {
  ALL_USERS_COHORT,
  COMPUTED_LIST_PROPERTIES_VALUES,
} from 'common/query-builder/query-block/properties-menu/constants';
import {
  getTempLookupTableMappings,
  lookupTablesStore,
  unregisterTempLookupTableMapping,
} from 'common/report/stores/lookup-tables';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {promisifyModal} from 'common/components/modal/utils';
import {DisplaySize} from 'common/components/tooltip/types';
import {Behavior, Segmentation} from 'common/types/reports/query';
import {showTour, TourId} from 'common/util/chameleon';
import {captureWorkflowTiming, QueryStartEvent, WorkflowComplete} from 'common/util/workflow-timing';
import {getActiveElement} from 'common/util/focusable';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {createFirstTimeEventFilter, isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {DEFAULT_FILTER_VALUE, PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {propIneligibleForMappingReason} from 'common/widgets/map-lookup-table-modal/util';
import type {MapLookupTableModalAttrs, PropertyItemWithValue} from 'common/widgets/map-lookup-table-modal';
import {getBucketPreview} from 'common/widgets/bucket-pane/numeric-bucket-pane/util';
import {
  MultiAttribution,
  MultiAttributionChangeEvent,
  MultiAttributionType,
} from 'common/widgets/multi-attribution-menu/types';
import {MenuChangeEvent} from 'common/components/select/types';
import {AttributionTypeToSpec} from 'common/widgets/multi-attribution-menu/constants';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';
import {propertySelectionToSegmentation} from 'common/query-builder/query-block/segment-menu/util';

import {generateFilterTrackingProps, transformFilterToPropertyItem} from '../query-block/filter-menu/util';
import {isCohortSegment, SegmentValue} from '../query-block/segment-menu/types';
import {isProperty, Property} from '../query-block/properties-menu/types';
import {SelectorTypeAggregationLabel} from '../types/shared-types';
import {getCountTypeForEntry, isAdvancedValueCalculation} from '../query-block/count-menu/util';
import {
  AggregationsMappings,
  commonAddEntryTooltipAttrs,
  ControlBarAction,
  ControlBarActionItems,
  ControlBarActionValue,
  ControlSelectEntryPoint,
  CUSTOM_BUCKET_NAMING,
  DefaultCustomBucket,
  NESTED_AGGREGATIONS_MENU_ITEMS,
  TutorialTooltips,
} from './constants';
import {template} from './template';
import css from './index.inline.styl';
import {getCustomizeGroupsTrackingProps, ResourceTrackingEvent, ResourceTrackingTypes, TrackingEvent} from './tracking';
import {
  Action,
  Calculation,
  CalculationTypes,
  CountBy,
  DROP_POSITION,
  EMPTY_EVENT_ENTRY,
  EMPTY_EXCLUSION_EVENT_ENTRY,
  EntryItem,
  EntryPart,
  EntrySectionUpdateEvent,
  EntryType,
  FiltersOperator,
  GroupEventBehaviors,
  InsightsCountType,
  isEventBasedType,
  isEventEntry,
  isFilterEntry,
  isGroupedEntry,
  isInlineCohortItem,
  isObjectEntry,
  isPropertyEntry,
  isPropertySelectorType,
  isSegmentEntry,
  isUserBasedType,
  QueryBehaviorEntry,
  QueryBucketableEntry,
  QueryEntry,
  QueryEntrySectionAttrs,
  QueryEntrySectionParams,
  QueryEventEntry,
  QueryFilterEntry,
  QueryGroupedEntry,
  QueryMetricEntry,
  QueryPropertyEntry,
  QuerySegmentEntry,
  QuerySuperEntry,
  RowAddress,
  RowLabelType,
  RowPrefixBehaviors,
  RowTypeComplexOption,
  SavedCohortItem,
  SectionOpenEntry,
  SelectorType,
  SelectorTypeAggregation,
  SelectorTypeDefinitions,
  SelectorTypesLabels,
  UpdateStatus,
} from './types';
import {EntryRow} from './row-types/base/types';
import {addEntryFromExternalSection, segmentToFilter} from './util/entry-transforms';
import {createEntryRow, getNewEmptyEntry} from './row-types/util';
import {applyFilterOperatorToFilter} from './row-types/filter/utils';
import {getBehaviorEntryTrackingProps, isMultiAttributionSegmentation} from './row-types/behavior/utils';
import type {FilterEntryRow} from './row-types/filter';
import type {SegmentEntryRow} from './row-types/segment-entry';
import {formatDataGroupLabel, getSelectedAggregationData, getSelectedGroupLabel} from './util';

interface QueryEntrySectionState {
  openEntry: SectionOpenEntry;
  hoveredEntry: SectionOpenEntry;
  dataGroups: DataGroup[];
  draggingStarted: boolean;
  draggingGripperIndex: number;
  draggingOverStepIndex: number;
  dropPosition: DROP_POSITION;
  hasRowWithHoveredContextMenu: boolean;
  placeholders: Map<number, [EntryPart, QuerySuperEntry]>;
  stagedRows: EntryRow[];
  stagedRow: EntryRow;
  // Renders custom entry prefixes for nested sections
  prefixRenderer: (params: {index: number; entry: QuerySuperEntry}) => VNode;
  suffixRenderer: (params: {index: number; entry: QuerySuperEntry}) => VNode;
  featureFlags: Set<ProjectFeatureFlag>;
  isTypeSelectOpen: boolean;
  hasOpenDropdown: boolean;
}

// Defines types of entities that can be added to the list
type TypeList = {[key in SelectorType]?: SectionItem<RowTypeComplexOption['value']>};

const COMPONENT_NAME = `mp-query-entry-section`;

const LIST_TYPES: TypeList = {
  [ResourceType.Cohort]: {
    label: capitalize(`${SelectorTypesLabels[ResourceType.Cohort]}`),
    value: ResourceType.Cohort,
    icon: `community`,
  },
  [ResourceType.Event]: {
    label: capitalize(`${SelectorTypesLabels[ResourceType.Event]}`),
    value: ResourceType.Event,
    icon: `event`,
  },
  [ResourceType.User]: {
    label: `Profile`,
    value: ResourceType.User,
    icon: `profile`,
  },
};

export interface QueryEntrySectionAttrsJsxProps {
  attrs: Partial<QueryEntrySectionAttrs>;
  params?: QueryEntrySectionParams;
  props?: {
    isStagingRow?: boolean | Partial<QuerySuperEntry>;
    prefixRenderer?: (params: {index: number; entry: QuerySuperEntry}) => VNode;
  };
}

export const queryStartedWorkflowComplete: WorkflowComplete<QueryEntrySection> = ({detail}) => {
  return detail.eventName === QueryStartEvent;
};

export class QueryEntrySection extends Component<
  QueryEntrySectionState,
  QueryEntrySectionAttrs,
  void,
  void,
  IronContextRegistry,
  QueryEntrySectionParams
> {
  private dispatchUpdate: (ev: EntrySectionUpdateEvent) => void;
  private nextEntryKey: number;
  private pendingQueryReason: QueryReason;
  private _projectStoreCallback;
  private _lookupTablesStoreCallback;
  public stickyHeaderElm: HTMLElement = null;

  /**
   * Defines a set of feature flags that are guaranteed to be the same during a given session
   * they are expected to change on a page reload or a re-render if the store value has changed
   */
  static CachedFeatureFlags = [];

  static get attrsSchema(): AttrsSchema<QueryEntrySectionAttrs> {
    return {
      ...super.attrsSchema,
      'section-title': `string`,
      'add-entry-label': `string`,
      'add-entry-button-label': `string`,
      'add-entry-button-tooltip-text': `string`,
      'add-entry-menu-width': {type: `number`, default: 110},
      'allow-cohort-filter': `boolean`,
      'autoactivate-placeholders': {
        type: `boolean`,
        description: `when set placeholders will automatically activate when added`,
      },
      'can-rename-step': `boolean`,
      'plot-style': `string`,
      'enable-enhanced-list-filters': `boolean`,
      'group-event-behavior': {
        type: `string`,
        enum: Object.values(GroupEventBehaviors),
        default: GroupEventBehaviors.None,
      },
      'data-group-id': `string`,
      'data-groups-enabled': `boolean`,
      // TODO (pablo): remove this attribute and pass it on the entry data field
      'date-range': `string`,
      'date-unit': `string`,
      'disable-add-entry': {type: `boolean`, description: `when set, hides the Add button from the row controls`},
      'disable-add-entry-reason': {
        type: `string`,
        description: `when set, disables + button in header with reason in tooltip`,
      },
      'disable-add-event-filters': {
        type: `boolean`,
        description: `when set, hides the Add filter button from the row's actions dropdown`,
      },
      'disable-clone-entry': `boolean`,
      'disable-delete-entry': `boolean`,
      'disable-first-time-event-filters': `boolean`,
      'disable-add-grouped-event-reason': `string`,
      'property-combine-mode': {type: `string`, default: null},
      'disable-map-to-lookup-table': `boolean`,
      'disabled-items': {
        type: `json`,
        default: null,
      },
      'enable-workspace-filter-restrictions': `boolean`,
      disabled: `boolean`,
      'entry-type': {type: `string`, default: EntryType.Event, enum: Object.values(EntryType)},
      'event-aggregator-functions': {
        type: `json`,
        description: `list of event aggregator function choices from primative math types`,
      },
      'row-label-type-for-exclusion-steps': {
        type: `string`,
        default: RowLabelType.Numeric,
        enum: Object.values(RowLabelType),
      },
      'property-aggregator-functions': {
        type: `json`,
        description: `list of property aggregator function choices from primative math types`,
      },
      entries: {
        type: `json`,
        default: [],
        description: `interface: EventEntry[]`,
      },
      'exclude-autotrack-events': {
        type: `boolean`,
        description: `whether to hide autotrack events from the events menu`,
      },
      'exclude-custom-events': `boolean`,
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
      'is-blocked-people': `boolean`,
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      'metrics-mode': {type: `boolean`, description: `allow property aggregation operations`},
      'behavior-mode': {type: `boolean`, description: `allow addition of inline behaviors`},
      'num-exclusion-steps': `number`,
      'project-has-cohorts': `boolean`,
      'project-has-people': `boolean`,
      'project-id': `number`,
      'user-id': `number`,
      readonly: `boolean`,
      'restrict-top-events-only': `boolean`,
      'row-drop-disabled': {
        type: `boolean`,
        description: `Whether drops should be disallowed despite being draggable`,
      },
      'row-draggable': {
        type: `boolean`,
        description: `Whether the events can be dragged to rearrange`,
      },
      'undraggable-rows': {
        type: `json`,
        default: [],
        description: `Array of indices for rows that cannot be dragged`,
      },
      'row-types': {
        type: `json`,
        default: [ResourceType.Event],
        description: `Default row types. Interface: RowTypeOption[]`,
      },
      'row-index-toggleable': {type: `boolean`, description: `Whether the row's label is toggleable`},
      'row-index-toggle-icon': `string`,
      'row-index': {
        type: `number`,
        default: null,
        description: `if this is a nested entry-section, provide its index in the parent section`,
      },
      'row-label-prefix': {
        type: `string`,
        description: `for a group of rows, indent labels and use this prefix`,
      },
      'row-label-prefix-icon': `string`,
      'row-label-type': {type: `string`, default: RowLabelType.Numeric, enum: Object.values(RowLabelType)},
      'row-limit': {type: `number`},
      'show-all-events': `boolean`,
      'show-any-event': `boolean`,
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'show-sessions': `boolean`,
      'show-top-events': `boolean`,
      'show-special-date-property': {
        type: `boolean`,
        description: `enables special date property and breakdown type for datetime`,
      },
      'hide-special-date-property-unit': {
        type: `boolean`,
        description: `disables the unit selection for special date property break-down`,
      },
      'show-special-dataset-property': `boolean`,
      'support-b2b-cohorts': `boolean`,
      'inline-cohorts-override': `json`,
      'time-display-context': {type: `string`, default: DisplayContext.FullLength},
      'track-name': `string`,
      'track-properties': {
        type: `json`,
        default: {},
        description: `If specified, adds extra properties to the track event. These will override ones from query-builder`,
      },
      autofocus: {
        type: `boolean`,
        description: `If true, entrySection action button will be focused when no entry is selected`,
      },
      'workspace-id': `number`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'event-names': {type: `json`, default: null},
      'other-group-by-options': {type: `json`, default: []},
      'other-filter-options': {type: `json`, default: []},
      'hide-events': `boolean`,
      'can-customize-numeric-buckets': {type: `boolean`},
      'can-use-multi-attribution': `boolean`,
      'enable-sticky-header': {
        type: `boolean`,
        description: `If true, entry section header will have a sticky header`,
      },
      'min-entries': {
        type: `number`,
        default: 0,
      },
      'empty-placeholder': {
        type: `json`,
        description: `interface: QuerySuperEntry`,
      },
    };
  }

  constructor() {
    super();
    this.nextEntryKey = 1;
    this.dispatchUpdate = debounce(
      (ev) => {
        ev.detail.queryReason = this.pendingQueryReason;
        delete this.pendingQueryReason;
        this._dispatchUpdate(ev);
      },
      100,
      {leading: true},
    );
  }

  get dragAndDrop() {
    return this.getContext(`dragAndDropManager`);
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      slowThreshold: 320, // p75 from https://mixpanel.com/s/2kFNfR

      contexts: [`dragAndDropManager` as const, `queryContextManager` as const, `currentProject` as const],
      defaultContexts: {queryContextManager: new QueryContextManager()},

      defaultState: {
        openEntry: {},
        hoveredEntry: {},
        prefixRenderer: null,
        suffixRenderer: null,
        dataGroups: [DEFAULT_DATA_GROUP],
        draggingStarted: false,
        draggingGripperIndex: null,
        draggingOverStepIndex: null,
        dropPosition: null,
        hasRowWithHoveredContextMenu: false,
        placeholders: new Map(),
        stagedRows: [],
        stagedRow: null,
        featureFlags: new Set<ProjectFeatureFlag>(),
        isTypeSelectOpen: false,
        hasOpenDropdown: false,
      } as QueryEntrySectionState,

      params: {
        listProperty: Object,
        disabledTypecastTypes: Array,
        rowActions: Object,
      },

      helpers: {
        Action,
        EntryPart,
        camelCase,
        ControlBarAction,
        ControlBarActionItems,
        GroupEventBehaviors,
        RowPrefixBehaviors,
        EntryType,
        ResourceType,
        RowLabelType,
        serializeAttributes,
        SelectorTypeDefinitions,
        isGroupedEntry,
        isStickyHeader: () => {
          return !!this.attr(`enable-sticky-header`);
        },
        onInsertStickyHeader: (el: HTMLElement) => {
          this.stickyHeaderElm = el;
        },
        getStickyHeaderElm: () => {
          return this.stickyHeaderElm;
        },
        allowDropAreas: (index) => {
          const dropAreas = new Set();
          if (!this.attr(`row-drop-disabled`) && !this.helpers.isAddingDisabled() && this.dragAndDrop.draggingEntry) {
            if (this.state.placeholders.size > 0 && this.state.placeholders.has(index)) {
              return dropAreas;
            }
            const result = addEntryFromExternalSection(this, this.dragAndDrop.draggingEntry, index, this.nextEntryKey);
            const {modifyExisting, entry} = result ?? {};

            // if a drop on this index would modify it, only allow "ON" type drops
            if (modifyExisting) {
              return new Set([DROP_POSITION.ON]);
            } else if (entry !== undefined) {
              // if the result on this index is a placeholder or the section is empty, only allow "HEADER" drop
              // else it can be place on either "ABOVE" or "BELOW" side
              if (
                (this.dragAndDrop.draggingEl !== this && this.createEntryRow(index, entry).isPlaceholder()) ||
                !this.getEntries().length
              ) {
                return new Set([DROP_POSITION.HEADER]);
              }
              dropAreas.add(DROP_POSITION.ABOVE);
              dropAreas.add(DROP_POSITION.BELOW);
            }
          }
          return dropAreas;
        },
        autoactivatePlaceholders: (force = false) => {
          if (!force && !this.attr(`autoactivate-placeholders`)) {
            return;
          }
          const firstPlaceholder = this.state.stagedRows.find((row) => row.isPlaceholder());
          if (firstPlaceholder && firstPlaceholder.defaultActiveEntryPart()) {
            this.setActiveEntry(firstPlaceholder.index, firstPlaceholder.defaultActiveEntryPart());
          }
        },
        canDeleteEntry: () => {
          const hasSingleListOfObjectsFilter = !!this.params.listProperty && this.getRows(true).length === 1;

          return this.attr(`disable-delete-entry`) === false && !hasSingleListOfObjectsFilter;
        },
        isDuplicateSupported: () => {
          return !this.attr(`disable-clone-entry`) && this.helpers.canAddRow();
        },
        canDuplicateEntry: (index: number) => {
          const row = this.getRows(true)[index];
          return (
            this.helpers.isDuplicateSupported() &&
            !this.isMultiAttributionSegmentationEntry(row) &&
            !this.isComputedListPropertyEntry(row)
          );
        },
        canUseRowType: (rowType: SelectorType) =>
          this.helpers.allowedRowTypes().some((type) => type === rowType || type[`value`] === rowType),
        allowedRowTypes: () => [
          ...this.attr(`row-types`),
          ...(this.helpers.groupedEventDropAllowed() ? [SelectorTypeDefinitions.GroupedEvent] : []),
        ],
        getSelectedTypeLabel: (entryType) => SelectorTypesLabels[entryType],
        isExclusionStepsSection: () => this.attr(`entry-type`) === EntryType.ExclusionEvent,
        isDroppingAbove: (index) =>
          !this.dragAndDrop.isDraggingOverChild(this) &&
          this.state.draggingOverStepIndex === index &&
          this.state.dropPosition === DROP_POSITION.ABOVE,
        isDroppingBelow: (index) =>
          !this.dragAndDrop.isDraggingOverChild(this) &&
          this.state.draggingOverStepIndex === index &&
          this.state.dropPosition === DROP_POSITION.BELOW,
        isDroppingOn: (index) =>
          !this.dragAndDrop.isDraggingOverChild(this) &&
          this.state.draggingOverStepIndex === index &&
          this.state.dropPosition === DROP_POSITION.ON,
        preventTracking: (ev: Event): void => preventTracking(ev),
        propagateEvent: (ev) => propagateEvent(this, ev),

        selectorTheme: () => qbStyleAttrs().selectorTheme,
        selectorType: () => qbStyleAttrs().selectorType,

        getAddListTypeForType: (type: SelectorType) => this.getAddListTypeItems()[type],
        getAddListTypeItems: () => Object.values(this.getAddListTypeItems()),
        getQueryBlockConfig: (entry: QuerySuperEntry, index) => this.getQueryBlockConfig(entry.type, index),

        hasBehavioralFrequencyFilter: (entry: QueryMetricEntry) =>
          this.attr(`behavior-mode`) && entry.event?.value === ALL_USERS_COHORT.value,

        groupedEventActionAllowed: () => this.attr(`group-event-behavior`) !== GroupEventBehaviors.None,
        groupedEventDropAllowed: () => this.attr(`group-event-behavior`) === GroupEventBehaviors.Nest,
        /** Callback for changes from child query-entry-section */
        onGroupedSectionUpdate: (index: number, ev: CustomEvent<{entries: QueryGroupedEntry['event']}>) => {
          const {entries} = ev.detail;
          const currentEntry = this.getEntries()[index];
          if (
            entries.length === 1 &&
            currentEntry.type === SelectorTypeDefinitions.GroupedEvent &&
            currentEntry.event.length > 1
          ) {
            this.updateEntries(Action.Ungroup, {
              entry: entries[0],
              index,
            });
          } else {
            this.updateEntries(Action.Edit, {
              entry: {
                ...currentEntry,
                type: SelectorTypeDefinitions.GroupedEvent,
                event: entries,
                filters: [],
              },
              index,
            });
          }
        },

        getNestedSectionAttrs: (): Partial<QueryEntrySectionAttrs> =>
          pick(this.attrs(), [
            `allow-cohort-filter`,
            `data-groups-enabled`,
            `data-group-id`,
            `can-customize-numeric-buckets`,
            `can-rename-step`,
            `disable-first-time-event-filters`,
            `enable-enhanced-list-filters`,
            `enable-workspace-filter-restrictions`,
            `exclude-autotrack-events`,
            `exclude-custom-events`,
            `exclude-dimension-properties`,
            `include-custom-properties`,
            `is-blocked-people`,
            `max-data-history-days`,
            `other-filter-options`,
            `readonly`,
            `show-all-events`,
            `show-any-event`,
            `hide-edit-controls`,
            `show-sessions`,
            `show-top-events`,
            `show-special-date-property`,
            `hide-special-date-property-unit`,
            `show-special-dataset-property`,
            `support-b2b-cohorts`,
            `workspace-id`,
            `user-id`,
            `project-id`,
            `project-as-of-date`,
            `project-has-people`,
            `project-has-cohorts`,
            `track-name`,
            `hide-events`,
          ]),

        isFirstTimeEventFilter: (filter: PropertyFilter) => isFirstTimeEventFilter(filter),
        getEntryFilters: (index: number) => {
          const filters = this.getEntry(index).filters;
          if (!this.attr(`behavior-mode`)) {
            return {
              [EntryPart.Filters]: filters,
            };
          }

          const [behavioralFrequencyFilters, finalFilters] = partition(
            filters,
            (filter) => filter.customProperty && isBehavioralCustomProperty(filter.customProperty),
          );

          return {
            [EntryPart.Filters]: finalFilters,
            [EntryPart.BehavioralFrequencyFilters]: behavioralFrequencyFilters,
          };
        },

        /** checks if an entry contains a first time event filter, in any position */
        hasFirstTimeEventFilter: (index: number) => this.getEntry(index)?.filters?.some(isFirstTimeEventFilter),

        onFiltersOperatorSelected: (operator: FiltersOperator, index): void => {
          const entry: QuerySuperEntry = {
            ...this.getEntry(index),
            filtersOperator: operator,
          };
          this.updateEntries(Action.Edit, {entry, index});
        },

        /** @todo [pablo] move all of behavior specific logic to a behavior entry type definition */
        onBehavioralFiltersOperatorSelected: (operator: FiltersOperator, index): void => {
          const entry: QueryBehaviorEntry = {
            ...this.getEntry(index),
            behavioralFiltersOperator: operator,
          };

          // when changing to a funnel behavior, all numeric entryFilters must switch to a
          // `is equal to` numeric frequency filter
          const behaviorEntry = this.getEntry<QueryBehaviorEntry>(index);
          if (behaviorEntry.behavioralFiltersOperator !== FiltersOperator.Then && operator === FiltersOperator.Then) {
            let [behavioralFrequencyFilters, filters] = partition(
              entry.filters,
              (filter) => filter.customProperty && isBehavioralCustomProperty(filter.customProperty),
            );

            behavioralFrequencyFilters = behavioralFrequencyFilters.map((filter) => {
              const filterOperator = [NumberOperator.EqualTo, NumberOperator.AtLeast].includes(
                filter.filterOperator as NumberOperator,
              )
                ? filter.filterOperator
                : NumberOperator.EqualTo;

              const updatedFilter = applyFilterOperatorToFilter(
                filterOperator,
                new PropertyFilter({
                  ...filter,
                  propertyType: PropertyType.Number,
                  propertyDefaultType: PropertyType.Number,
                  customProperty: {
                    name: ``,
                    description: ``,
                    // resets aggregations to defaults
                    behavior: {
                      ...filter.customProperty.behavior,
                      property: null,
                      aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
                    },
                  },
                  filterValue:
                    filter.propertyType !== PropertyType.Number ? DEFAULT_FILTER_VALUE.number : filter.filterValue,
                }),
              );

              return updatedFilter.getAttributes();
              // }

              // return filter;
            }) as PropertyFilter[];

            entry.filters = [...filters, ...behavioralFrequencyFilters];
          }

          this.updateEntries(Action.Edit, {entry, index});
        },

        onControlSelect: (value: ControlBarActionValue, index) => {
          switch (value.action) {
            case ControlBarAction.Filter:
              return this.helpers.showPlaceholderForEntry(EntryPart.Filters, index);
            case ControlBarAction.FirstTimeFilter:
              return this.applyFirstTimeEventFilter(index);
            case ControlBarAction.Rename:
              return this.promptRename(index);
            case ControlBarAction.Duplicate:
              return this.helpers.onEventActionClick(Action.Clone, index);
            case ControlBarAction.Delete:
              return this.helpers.onEventActionClick(Action.Remove, index);
            case ControlBarAction.CustomizeBuckets:
            case ControlBarAction.CustomBreakdown:
              return this.onCustomizeBuckets(index, ControlSelectEntryPoint.ControlBar);
            case ControlBarAction.AddMultiAttribution:
              return this.onOpenMultiAttributionMenu(index);
            case ControlBarAction.AddMultiAttributionBehaviorFilter:
              return this.openMultiAttributionBehaviorFilterValue(index);
            case ControlBarAction.AddGroupedEvent:
              return this.addOrConvertToGroupedEvent(index);
            case ControlBarAction.MapLookupTable:
              return this.onMapLookupTable(index);
            case ControlBarAction.UnmapLookupTable:
              return this.onUnmapLookupTable(index);
            case ControlBarAction.Typecast:
              return this.doTypecast(index, value.toType);
            default:
              assertUnreachable(value);
          }
        },

        // eslint-disable-next-line sonarjs/cognitive-complexity
        getEntryOverflowControls: (index: number): Array<Section<ControlBarActionValue>> => {
          const canDeleteEntry = this.hasNewQBUI() && this.helpers.canDeleteEntry();
          const row = this.getRows(true)[index];
          let rowItems: Array<Section<ControlBarActionValue>> = [];

          if (!row?.entry?.event) {
            rowItems = [];
          } else {
            const {entry} = row;
            const isPlaceholder = row.isPlaceholder();
            const eventValue = (entry as QueryEventEntry).event.value;
            const isTopEvents = eventValue === SPECIAL_EVENTS.TOP_EVENTS;
            const filteringCapabilities = this.getRowFilteringCapabilities(row);

            const canRenameStep =
              !isPlaceholder &&
              !isInlineCohortItem(entry.event as SavedCohortItem) &&
              this.attr(`can-rename-step`) &&
              !isTopEvents;

            const canDuplicate = !isPlaceholder && this.helpers.canDuplicateEntry(index);
            const canCustomizeBuckets = this.canCustomizeBuckets(index);
            const propType = this.getPropertyType(index);

            let canAddGroupedEvent = entry.type === ResourceType.Event && this.helpers.groupedEventActionAllowed();

            if (canAddGroupedEvent && this.attr(`group-event-behavior`) === GroupEventBehaviors.Append) {
              const lastRow = last(this.getRows());
              canAddGroupedEvent = !lastRow.isPlaceholder();
            }

            const isAddGroupedEventDisabled =
              canAddGroupedEvent && this.attr(`disable-add-grouped-event-reason`) && !this.isStagedEntry;
            const disabledAddGroupedEventReason = isAddGroupedEventDisabled
              ? this.attr(`disable-add-grouped-event-reason`)
              : null;

            rowItems = [
              this.helpers.makeSection(
                ...row.overflowControls(),
                // filter
                filteringCapabilities.canApplyFilter &&
                  !this.helpers.hasBehavioralFrequencyFilter(entry as QueryMetricEntry) &&
                  ControlBarActionItems[ControlBarAction.Filter],
                // event 1st time filters
                filteringCapabilities.canApplyFirstTimeEventFilter && {
                  ...ControlBarActionItems[ControlBarAction.FirstTimeFilter],
                  ...(filteringCapabilities.firstTimeFilterDisabled
                    ? {
                        tooltip: {
                          text: `To apply 1st time filter, select a different event`,
                        },
                        isDisabled: true,
                      }
                    : {}),
                },
              ),
              this.helpers.makeSection(
                this.canAddMultiAttribution(index) && ControlBarActionItems[ControlBarAction.AddMultiAttribution],
              ),
              this.helpers.makeSection(
                // funnels group events
                canAddGroupedEvent && {
                  ...ControlBarActionItems[ControlBarAction.AddGroupedEvent],
                  ...(isAddGroupedEventDisabled
                    ? {
                        tooltip: {
                          text: disabledAddGroupedEventReason,
                        },
                        isDisabled: true,
                      }
                    : {}),
                },
                // rename event
                canRenameStep && ControlBarActionItems[ControlBarAction.Rename],
                // typecasting
                this.canTypecast(row) &&
                  ControlBarActionItems[ControlBarAction.Typecast](propType, {
                    disabledTypes: this.params.disabledTypecastTypes,
                  }),
                // map to lookup table
                this.canMapLookupTable(row) && ControlBarActionItems[ControlBarAction.MapLookupTable],
                // unmap lookup table
                this.canUnmapLookupTable(row) && ControlBarActionItems[ControlBarAction.UnmapLookupTable],
                // filter attribution touchpoints by property value
                this.getMultiAttributionBehavior(index) &&
                  ControlBarActionItems[ControlBarAction.AddMultiAttributionBehaviorFilter],
                // customize range
                canCustomizeBuckets &&
                  (this.usingNewBucketing(index)
                    ? ControlBarActionItems[ControlBarAction.CustomBreakdown](propType)
                    : ControlBarActionItems[ControlBarAction.CustomizeBuckets]),
              ),
              this.helpers.makeSection(
                // delete row
                canDeleteEntry && ControlBarActionItems[ControlBarAction.Delete],
                // duplicate row
                canDuplicate && ControlBarActionItems[ControlBarAction.Duplicate],
              ),
            ].filter(Boolean);
          }

          //Filter out empty sections
          return rowItems.filter((rowItem) => rowItem.items.length > 0);
        },

        /** Just structures the provided inputs into the section format */
        makeSection: (...rowItems) => {
          return {
            items: rowItems.filter(Boolean),
          };
        },

        /** Disable the adding button when row-limit is 1, the placeholder row is added */
        showAddButton: () => {
          if (this.attr(`group-event-behavior`) === GroupEventBehaviors.Append) {
            return false;
          }

          return this.helpers.canAddRow();
        },

        /** Check to see if we can add a row */
        canAddRow: () => {
          if (this.attr(`readonly`) || this.attr(`disable-add-entry-reason`)) {
            return false;
          }

          const rowLimit = this.attr(`row-limit`);
          return rowLimit > 0 ? this.helpers.getEntries().length < rowLimit : true;
        },

        /** Aborts staging new row when delete action is performed */
        onEventActionClick: (action: Action, index: number) => {
          if (action === Action.Remove) {
            this.removeEntry(index);
          } else {
            this.updateEntries(action, {index});
          }
        },

        isStagedRow: (index: number) => this.isStagedEntry(index),
        isNestedSection: () => this.attr(`group-event-behavior`) === GroupEventBehaviors.Append,

        /** Disables adding until last row is commited */
        isAddingDisabled: () => {
          return (
            this.getRows(true).some((row) => row.isPlaceholder()) ||
            this.attr(`disabled`) ||
            this.helpers.canAddRow() === false
          );
        },

        getRowLabel: (index: number): string => {
          switch (this.attr(`row-label-type`)) {
            case RowLabelType.Alpha:
              return getAlphaLabelFromIndex(index);
            case RowLabelType.Numeric:
              return `${index + 1}`;
            default:
              return `-`;
          }
        },

        getRowIcon: (index: number) => {
          const entry = this.getEntry(index);

          // only toggle if label is provided and event is set as toggled.
          if (this.attr(`row-index-toggle-icon`) && entry && entry.entryIndexLabelToggle) {
            return this.attr(`row-index-toggle-icon`);
          } else {
            return null;
          }
        },

        getContextPanelParentEl: (index: number) =>
          this.hasNewQBUI() ? this.el.querySelectorAll(`[elref="entryRow"]`)[index] : null,

        showPlaceholderForEntry: (type: EntryPart, index: number, entry?: QuerySuperEntry) => {
          const {placeholders} = this.state;
          placeholders.set(index, [type, entry]);

          this.update({placeholders});
        },
        getEntry: (index): QuerySuperEntry => this.getEntries({appendPlaceholder: true})[index],
        getEntries: (): QuerySuperEntry[] => this.getEntries({appendPlaceholder: true}),

        setActiveEntry: (state: `open` | `close`, index: number, part: EntryPart): void => {
          const {index: activeIndex, part: activePart} = this.state.openEntry;

          if (state === `close` && this.state.placeholders.get(index)?.includes(part)) {
            const placeholders = new Map(this.state.placeholders);
            placeholders.delete(index);
            this.update({placeholders});
          }

          if (state === `open`) {
            this.setActiveEntry(index, part);
          } else if (activeIndex === index && activePart === part) {
            // If a user adds a new event/breakdown/filter but does not pick one and clicks out of a menu,
            // abandon the added event/breakdown/filter
            const stagedRow = this.state.stagedRow;
            if (part === EntryPart.Value && stagedRow && !stagedRow.isIntermediatePlaceholder()) {
              this.abortRowStage();
            } else {
              this.setActiveEntry(null, null);
            }
          }
        },

        onSelectMenuChange: (ev: MenuChangeEvent, index: number, part: EntryPart) => {
          const {isOpen} = ev.detail;
          const status = isOpen ? `open` : `close`;

          this.helpers.setActiveEntry(status, index, part);
        },

        /** Changes type of staged entry */
        onAddTypeSelectMenuChange: (ev: CustomEvent) => this.update({isTypeSelectOpen: ev.detail.isOpen}),

        isDragEnabled: (index: number): boolean => {
          const hasNewQBUI = this.hasNewQBUI();
          const dragEnabled =
            !isFirstTimeEventFilter(this.helpers.getEntries()[index].event as PropertyFilter) &&
            !this.state.hasOpenDropdown &&
            this.helpers.isDragEnabledNondynamic(index);
          return hasNewQBUI ? dragEnabled : dragEnabled && !this.helpers.isAddingDisabled();
        },
        // Nondynamic doesnt rely on state
        isDragEnabledNondynamic: (index: number): boolean =>
          this.attr(`row-draggable`) &&
          this.attr(`row-limit`) !== 1 &&
          !this.attr(`undraggable-rows`).includes(index) &&
          !this.attr(`disabled`),

        /**
         * When context menu is open, `draggable=true` prevents users from selecting text
         * within the context panel. We temporarily set `draggable=false` when child context
         * menu is hovered
         */
        handleContextMenuHoverChange: (isHovering: boolean) => {
          this.update({hasRowWithHoveredContextMenu: isHovering});
        },

        isDraggable: () => {
          return this.attr(`row-draggable`) && this.attr(`row-limit`) !== 1;
        },

        isDraggingIndex: (index) => index === this.state.draggingGripperIndex && this.state.draggingStarted,
        isDraggingGripperIndex: (index) => index === this.state.draggingGripperIndex,

        /**
         * Starts a drag operation only when it initiates from a gripper control,
         * this is achieved by setting the event index when a gripper is pressed
         */
        onEntryStepDragStart: (ev: DragEvent, fromIndex: number): void => {
          if (this.state.draggingGripperIndex !== fromIndex) {
            return ev.preventDefault();
          }
          ev.stopPropagation();
          this.update({draggingStarted: true});

          getActiveElement()?.blur();

          // only should be blank in safari tests
          if (ev.dataTransfer) {
            this.writeRowAddress(ev, fromIndex);
            ev.dataTransfer.effectAllowed = `copyMove`;
            ev.dataTransfer.dropEffect =
              ev.altKey && this.helpers.canDuplicateEntry(this.state.draggingGripperIndex) ? `copy` : `move`;
          }

          // Fire a custom event so all the section rerender accordingly (greying out if not a valid drop area)
          this.dragAndDrop.setDraggingInfo(this, this.getDraggingEntry());
        },

        onEntryStepDragEnter: (ev: DragEvent, index: number): void => {
          ev.preventDefault();
          ev.stopPropagation();
          this.update({draggingOverStepIndex: index});
        },
        onEntryStepDragLeave: (): void => {
          this.dragAndDrop.removeDragoverEl(this);
          this.update({draggingOverStepIndex: null});
          this.dispatchEvent(new CustomEvent(`entryDragLeave`, {bubbles: true, composed: true}));
        },

        onEntryStepDragOver: (ev: DragEvent, index: number): void => {
          ev.preventDefault();
          this.dragAndDrop.addDragoverEl(this);
          ev.dataTransfer.dropEffect =
            ev.altKey && this.helpers.canDuplicateEntry(this.state.draggingGripperIndex) ? `copy` : `move`;
          this.update({draggingOverStepIndex: index, dropPosition: this.getDropPosition(ev, index)});
          this.dispatchEvent(new CustomEvent(`entryDragOver`, {bubbles: true, composed: true, detail: {index}}));
        },

        onEntryStepDragClear: (): void => {
          this.dragAndDrop.clearDraggingInfo();
          this.update({
            draggingStarted: false,
            draggingGripperIndex: null,
            draggingOverStepIndex: null,
            dropPosition: null,
          });
        },

        onEntryStepDrop: (ev: DragEvent, dropIndex: number): void => {
          ev.preventDefault();
          ev.stopPropagation();
          const fromAddress = ev.dataTransfer ? this.readRowAddress(ev) : ([-1] as RowAddress);

          // Insert after the drop target index if dropping below (assuming we have at least one entry, otherwise it should stay 0)
          // Techincially this calls the transform twice TODO bucc think of how to make more efficient w/o relying on dragover
          if (this.getDropPosition(ev, dropIndex) === DROP_POSITION.BELOW && this.getEntries().length) {
            dropIndex++;
          }

          // Don't do anything if self drop
          const immovableEntryKeys = this.helpers
            .getEntries()
            .map((entry) => entry.entryKey)
            .slice(Math.max(dropIndex - 1, 0), dropIndex + 1);
          if (
            (!ev.altKey &&
              this.dragAndDrop.draggingEl === this &&
              immovableEntryKeys.includes(this.dragAndDrop.draggingEntry.entryKey)) ||
            (this.shadowRoot.contains(this.dragAndDrop.draggingEl) && fromAddress[0] === dropIndex)
          ) {
            this.helpers.onEntryStepDragClear();
            return;
          }

          this.handleEntryStepDrop(fromAddress, dropIndex, ev.altKey);
          this.helpers.onEntryStepDragClear();
        },

        onEventSelected: (ev: CustomEvent, index: number): void => {
          const {item, trackingProps, queryReason} = ev.detail;
          this.onEntryEventSelected(index, item, {trackingProps, queryReason});
        },

        onGroupEntryIndexLabelClicked: () => {
          this.dispatchEvent(new CustomEvent(`entryIndexLabelClicked`));
        },

        onEntryIndexLabelClicked: (index: number) => {
          const entryAtIndex = this.attr(`row-index-toggleable`) && this.getEntry(index);
          if (entryAtIndex) {
            const entry: QuerySuperEntry = {
              ...entryAtIndex,
              entryIndexLabelToggle: !entryAtIndex.entryIndexLabelToggle,
            };
            this.updateEntries(Action.Edit, {entry, index});
            this.dispatchTrack(TrackingEvent.EventIndexLabelClicked, {
              ...this.getEntryTrackingProps(index),
              'Previous Label': this.helpers.getRowLabel(index),
            });
          }
        },

        /**
         * When interacting with non-draggable elements like dropdowns or inputs, we want to
         * disable the behavior of dragging from any point inside the item entry
         */
        onGripperMouseDown: (index: number, ev: MouseEvent): void => {
          const isWithinUndraggableEls = eventPathContainsSelectors(ev, [`mp-drop-menu`, `mp-input`]).containsSelectors;
          this.helpers.isDragEnabled(index) &&
            this.update({draggingGripperIndex: isWithinUndraggableEls ? null : index});
        },

        onDateRangeChange: (ev: CustomEvent, index: number) => {
          const entryAtIndex = this.getEntry(index);
          if (this.helpers.isDateRangeType(entryAtIndex.type)) {
            const entry = {
              ...entryAtIndex,
              dateRange: (ev.detail as DateRange).getAttributes(),
            };
            this.updateEntries(Action.Edit, {entry, index});
            this.trackAction(index, entry, Action.SelectDateRange, ev.detail.props);
          }
        },

        getDefaultDateValue: () =>
          this.attr(`date-range`) ||
          DateRangeFactory.createDateRange({}, {displayContext: this.attr(`time-display-context`)}).getAttributes(),

        isEventBasedType: (type: SelectorType) => isEventBasedType(type),

        isDateRangeType: (type: SelectorType) =>
          [SelectorTypeDefinitions.Behavior, SelectorTypeDefinitions.EventLaunch].includes(
            type as SelectorTypeDefinitions,
          ) || this.helpers.getAddListTypeForType(type)?.includeDateRange,

        // TODO: There's tracking split among this function and in updateEntries. Merge the tracking
        // logic to avoid confusion.
        trackEntrySelectorEvent: (ev: CustomEvent, index: number) => {
          preventTracking(ev);

          // This is a hack due to (what appears to be) a race condition surfaced in our WCT tests.
          // I spent a few hours trying to identify root cause but the system is sufficiently complex
          // enough and I'm sufficiently ignorant enough that I couldn't figure it out. The bug manifests itself
          // as this.helpers.getEntries() is an empty list.
          if (this.helpers.getEntries().length <= index) {
            return;
          }

          const type = this.helpers.getEntries()[index].type;
          const commonProps = {
            'Entry index': index,
            'Entry type': type,
          };

          const {eventName, eventPrefix, props} = ev.detail;
          if (eventName === `${eventPrefix}${QueryBlockTrackingEvent.HOVER_CONTEXT_PANEL}`) {
            ev.stopPropagation();
            const eventName = ev.detail.eventName.replace(eventPrefix, ``);
            this.dispatchTrack(eventName, {
              ...props,
              ...commonProps,
            });
            return;
          }

          if (!ev.cancelable || isPropertySelectorType(type)) {
            // Filter and segment actions are tracked elsewhere
            return;
          }

          let trackingAction = null;

          switch (eventName) {
            case `[mp-query-builder] Add property filter`:
              trackingAction = Action.AddFilter;
              break;
            case `[mp-query-builder] Edit property filter`:
              trackingAction = Action.EditFilter;
              break;
            case `[mp-query-builder] Remove property filter`:
              trackingAction = Action.RemoveFilter;
              break;
            case `[mp-query-builder] Select date range`:
              trackingAction = Action.SelectDateRange;
              break;
            case `[mp-query-builder] Select Event`:
            case `[mp-query-builder] Select event`:
            case `[mp-query-builder] Select Property`:
            case `[mp-query-builder] Select segment`:
              trackingAction = Action.Select;
              break;
            default:
              throw new Error(`tracking not defined for entry selector event: ${eventName}`);
          }

          this.dispatchTrack(this.getTrackingEvent(type, trackingAction), {
            ...props,
            ...commonProps,
          });
        },

        overflowControlsContextPanelTransform: (item: SectionItem) => ControlBarActionItems[item?.value]?.tooltipData,

        /**
         * TODO(pablo): this needs to go away.
         * Exclusion steps related fns.
         */
        exclusionSteps: {
          getExclusionStep: (index: number) => {
            const entry = this.getEntry(index);
            return {isEntireRange: entry.excludeAll, range: entry.excludeRange};
          },

          getNumExclusionSteps: (): number => {
            return this.attr(`num-exclusion-steps`);
          },

          getExclusionStepsRowLabelType: (): string => {
            return this.attr(`row-label-type-for-exclusion-steps`);
          },

          onExclusionStepSelected: (ev: CustomEvent, index: number) => {
            const {isEntireRange: excludeAll, range: excludeRange}: RangeSelection = ev.detail;

            const entryUpdate: QuerySuperEntry = {
              ...this.getEntry(index),
              excludeAll,
              excludeRange,
            };
            this.updateEntries(Action.Edit, {entry: entryUpdate, index});

            this.dispatchTrack(TrackingEvent.EditExclusionStepRange, {
              'Exclude all': excludeAll,
              'Exclude from': excludeRange.from || null,
              'Exclude to': excludeRange.to || null,
            });
          },
        },

        getCustomBucketPaneAttributes: () => {
          const activeIndex = this.state.openEntry.index;
          const bucket = this.getCustomBucketByIndex(activeIndex);
          const isSegmentSection = isSegmentEntry(this.getEntry(activeIndex));
          const defaultBucket =
            isSegmentSection &&
            this.getContext(`queryContextManager`).segmentationMetadatas?.[activeIndex]?.numericBucket;
          const selectedBucket = bucket || defaultBucket || DefaultCustomBucket;
          const entry = this.getEntry(activeIndex);
          const customGroups = isSegmentEntry(entry) && (entry.event as Segmentation).customGroups;
          return {
            'event-names': this.attr(`event-names`),
            'max-num-buckets': 1000,
            'reset-bucket': !!bucket || !!customGroups,
            'selected-bucket': selectedBucket,
            'selected-unit': `units`,
            type: `units`,
            header: `Customize Range`,
          };
        },
        onCustomizeBucketsMenuChange: (ev, index) => {
          if (ev.detail.isOpen) {
            this.onCustomizeBuckets(index, ControlSelectEntryPoint.CustomBucketSubItem);
          } else {
            this.abortRowStage();
          }
        },

        onChangeSelectedCustomBucket: (ev) => {
          const activeIndex = this.state.openEntry.index;
          const prevCustomBucket = this.getCustomBucketByIndex(activeIndex);
          this.updateCustomBucket(activeIndex, ev.detail);
          const customBucket = this.getCustomBucketByIndex(activeIndex);
          this.dispatchTrack(TrackingEvent.ApplyCustomBucket, {
            ...this.getEntryTrackingProps(activeIndex),
            ...this.getCustomBucketTrackingProps(customBucket),
            'Is editing': !!prevCustomBucket,
          });
          this.abortRowStage();
        },

        onResetCustomBucket: () => {
          const activeIndex = this.state.openEntry.index;
          this.updateCustomBucket(activeIndex, null);
          this.dispatchTrack(TrackingEvent.ResetCustomBucket, {
            ...this.getEntryTrackingProps(activeIndex),
          });
          this.abortRowStage();
        },
        onUpdateBucketProperty: ({detail}) => {
          const activeIndex = this.state.openEntry.index;
          const entry = this.getEntry(activeIndex);

          //TODO: Akshay (Customize Grouping) - add tracking event for updates on "apply" here - one event regardless of prop type (nums 3,4 on notion)
          this.dispatchTrack(TrackingEvent.ApplyCustomBucket, {
            ...getCustomizeGroupsTrackingProps(detail.segment),
            ...this.getEntryTrackingProps(activeIndex),
          });

          // For some reason customBucket can exist in both the event and outer section, so set them
          // to be the same. TODO make it only exist in the event
          const updatedEntry = {...entry, event: detail.segment, customBucket: detail.segment.customBucket};
          this.updateEntries(Action.Edit, {entry: updatedEntry, index: activeIndex});
          this.abortRowStage();
        },

        /** helpers are this bound, a little helper function that simply re-draws the UI when any store values change */
        updateAfterStoreValueChange: () => this.update(),

        getAddEntryButtonTooltipAttrs: () => {
          if (this.attr(`disable-add-entry-reason`)) {
            return {text: this.attr(`disable-add-entry-reason`), ...commonAddEntryTooltipAttrs};
          }

          if (this.helpers.isAddingDisabled()) {
            return null;
          }

          if (this.attr(`add-entry-button-tooltip-text`)) {
            return {text: this.attr(`add-entry-button-tooltip-text`), ...commonAddEntryTooltipAttrs};
          }

          const type = Object.values(this.getAddListTypeItems())[0].value;
          return TutorialTooltips[type] ?? null;
        },
        onAutofocusableElAdded: (el) => {
          if (this.attr(`autofocus`)) {
            requestAnimationFrame(() => el.focus());
          }
        },
      },
    };
  }

  /**
   * Sets the component to a row staging mode and optionally sets additional entry fields
   */
  set isStagingRow(partialStagedRow: boolean | Partial<QuerySuperEntry>) {
    const {stagedRow} = this.state;

    if (partialStagedRow && !stagedRow) {
      if (this.state.stagedRows.at(-1)?.isPlaceholder()) {
        // This branch is needed for list-of-objects breakdown -> drag to filter section. Before this change a second filter
        // row was added (and the second row's property-menu was eager opened) when the first row was missing a filter value
        this.helpers.autoactivatePlaceholders(true);
      } else {
        const index = this.state.stagedRows.length;
        const type = Object.values(this.getAddListTypeItems())[0].value;
        const entry = {
          ...this.getNewEntry(stagedRow?.entry.type ?? type, {...stagedRow?.entry}),
          ...(typeof partialStagedRow === `boolean` ? {} : (partialStagedRow as QuerySuperEntry)),
        };

        // avoid auto opening the first staged row
        const entryRow = this.createEntryRow(index, entry);
        this.update({stagedRow: entryRow});
        entryRow.defaultActiveEntryPart() && this.setActiveEntry(index, entryRow.defaultActiveEntryPart());
      }
    }
  }

  /** Construct a row address for this index */
  getRowAddress(index: number): RowAddress {
    const rowAddr = [index];
    if (Number.isInteger(this.attr(`row-index`))) {
      rowAddr.unshift(this.attr(`row-index`));
    }

    return rowAddr as RowAddress;
  }

  /** Write index as the row address into ev.dataTransfer */
  writeRowAddress(ev: DragEvent, index: number): void {
    ev.dataTransfer.setData(`application/json`, JSON.stringify(this.getRowAddress(index)));
  }

  /** Read a row address off of a DragEvent dataTransfer */
  readRowAddress(ev: DragEvent): RowAddress {
    return JSON.parse(ev.dataTransfer.getData(`application/json`)) as RowAddress;
  }

  public async connectedCallback() {
    // blocking connectedCallback to fetch a resource is almost always a bad idea,
    // we make an exception for this component to locally obtain a copy of cached
    // project feature flags to prevent flashes of different UIs throughout a single
    // session, whenever a new report is loaded, the refreshed feature flags will be
    // used to render the UI
    if (QueryEntrySection.CachedFeatureFlags.length) {
      const {stagedRow} = this.state;
      await this.getFeatureFlags();
      this._projectStoreCallback = null;
      if (stagedRow) {
        this.update({stagedRow: this.createEntryRow(stagedRow.index, stagedRow.entry)});
      }
    }

    this.updateStagedRowsFromEntries();
    super.connectedCallback();

    this.fetchDataGroups();
    this._lookupTablesStoreCallback = ({fromUpdate}) => {
      if (fromUpdate) {
        // only refresh on manual update (a temp mapping is registered / unregistered)
        this.update();
      }
    };
    lookupTablesStore.subscribe(this._lookupTablesStoreCallback);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._projectStoreCallback && currentProjectStore.unsubscribe(this._projectStoreCallback);
    this._projectStoreCallback = null;
    lookupTablesStore.unsubscribe(this._lookupTablesStoreCallback);
    this._lookupTablesStoreCallback = null;
  }

  public attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);

    if (attr === `row-types` && !isEqual(JSON.parse(oldValue), JSON.parse(newValue))) {
      this.abortRowStage();
    } else if (attr === `entries` && JSON.parse(newValue).length >= this.attr(`min-entries`)) {
      this.updateStagedRowsFromEntries();
      this.abortRowStage();
    } else if ([`project-id`, `workspace-id`].includes(attr) && this.initialized) {
      this.fetchDataGroups();
    }
  }

  getFeatureFlags() {
    return new Promise((resolve) => {
      this._projectStoreCallback = () => {
        const featureFlags = new Set<ProjectFeatureFlag>();
        for (const flag of QueryEntrySection.CachedFeatureFlags) {
          currentProjectStore.value?.hasFeatureFlag(flag) && featureFlags.add(flag);
        }

        this.update({featureFlags});

        currentProjectStore.unsubscribe(this._projectStoreCallback);
        resolve(true);
      };
      currentProjectStore.subscribe(this._projectStoreCallback);
      currentProjectStore.fetch({projectId: this.attr(`project-id`)});
    });
  }

  public getDraggingEntry(): QuerySuperEntry {
    if (this.state.draggingGripperIndex >= 0) {
      return cloneDeep(this.helpers.getEntries()[this.state.draggingGripperIndex]);
    }
    return null;
  }

  mapEntriesToStagedRows(entries: QuerySuperEntry[]): EntryRow[] {
    return entries.map((entry, index) => {
      const entryKey =
        index < this.state.stagedRows.length ? this.state.stagedRows[index].entry.entryKey : this.nextEntryKey++;
      const type = entry.type || ResourceType.Event;
      let aggregationOperator = entry.aggregationOperator;
      // If the current entry is empty, set the default operator to unique in new count menu
      // This part can be removed once we no longer need hasNewCountMenu flag
      if (this.isEmptyPlaceholder(index, entry) && this.hasNewCountMenu()) {
        aggregationOperator = PRIMITIVE_MATH_TYPES.UNIQUE;
      }
      const entryRow = this.createEntryRow(index, {
        ...entry,
        ...(aggregationOperator && {aggregationOperator}),
        type,
        entryKey,
        ...(isUserBasedType(entry)
          ? {
              readOnlyEvent: true,
              event: {
                ...entry.event,
                // populate data group user label
                ...(entry.dataGroupId ? {label: `All ${this.getSelectedGroupLabel()}(s)`} : {}),
              },
            }
          : {}),
      } as QuerySuperEntry);
      return entryRow;
    });
  }

  getEmptyEntry() {
    const defaultEmptyEntry = this.attr(`empty-placeholder`);
    // Set "Unique" as default aggregation for new count menu
    // This part can be removed once we no longer need hasNewCountMenu flag
    if (this.hasNewCountMenu()) {
      defaultEmptyEntry.aggregationOperator = PRIMITIVE_MATH_TYPES.UNIQUE;
    }
    return defaultEmptyEntry;
  }

  updateStagedRowsFromEntries() {
    // If the number of entries is less than the mininum number of the required entries,
    // fill in the missing blocks with an empty placeholder entry
    if (this.attr(`min-entries`) && this.attr(`entries`).length < this.attr(`min-entries`)) {
      // If the missing blocks are already filled, skip
      if (this.state.stagedRows.length >= this.attr(`min-entries`)) {
        return;
      }
      // If the missing blocks are not filled, fill in with an empty placeholder entry
      const defaultEmptyEntry = this.getEmptyEntry();
      if (defaultEmptyEntry) {
        const stagedRows = this.mapEntriesToStagedRows(this.attr(`entries`));
        for (let i = 0; i < this.attr(`min-entries`) - this.state.stagedRows.length; i++) {
          stagedRows.push(this.createEntryRow(this.attr(`entries`).length + i, defaultEmptyEntry));
        }
        this.update({
          stagedRows,
        });
      }
      return;
    }
    const stagedRows = this.mapEntriesToStagedRows(this.attr(`entries`));
    this.update({
      placeholders: new Map(),
      stagedRows,
    });
    this.helpers.autoactivatePlaceholders();
  }

  public getDropPosition(ev: DragEvent, toIndex: number) {
    const dropTarget = ev.currentTarget as HTMLElement;

    // simulate a drop to check the special "on" state
    const result = addEntryFromExternalSection(this, this.dragAndDrop.draggingEntry, toIndex, this.nextEntryKey + 1);
    const {modifyExisting, entry} = result ?? {};
    if (modifyExisting) {
      return DROP_POSITION.ON;
    }

    if (
      (this.dragAndDrop.draggingEl !== this && this.createEntryRow(toIndex, entry).isPlaceholder()) ||
      !this.getEntries().length
    ) {
      return DROP_POSITION.HEADER;
    }

    const rect = dropTarget.getBoundingClientRect();
    return ev.clientY - (rect.top + rect.height / 2) < 0 ? DROP_POSITION.ABOVE : DROP_POSITION.BELOW;
  }

  /**
   * Shifts the step at fromIndex to toIndex,
   * then triggers an event update to requery the data
   */
  public handleEntryStepDrop(fromIndex: RowAddress, toIndex: number, copy = false): void {
    const draggingEntry = this.dragAndDrop.draggingEntry;
    const draggingEl = this.dragAndDrop.draggingEl;
    const result = addEntryFromExternalSection(this, draggingEntry, toIndex, this.nextEntryKey++);
    const {entries, entry, index} = result;

    const trackingProps = {
      Clone: copy,
      'From index': fromIndex.join(`.`),
      'To index': this.getRowAddress(toIndex).join(`.`),
      'From section': draggingEl?.attr(`track-properties`)?.sectionName,
      'To section': this.attr(`track-properties`)?.sectionName,
    };

    if (entry && this.createEntryRow(index, entry).isPlaceholder()) {
      if (result.modifyExisting) {
        // set placeholders if dragging property entry to inline filter
        const filterEntry = isSegmentEntry(draggingEntry) ? segmentToFilter(draggingEntry) : draggingEntry;
        this.helpers.showPlaceholderForEntry(EntryPart.Filters, index, filterEntry);
      } else {
        this.isStagingRow = entry;
      }
    } else {
      this.commitAndTrackUpdate(entries, entry, {index, trackingProps, action: Action.Drag});
    }

    if (draggingEntry && this.createEntryRow(index, draggingEntry).isPlaceholder()) {
      draggingEl.abortRowStage();
    } else {
      // If we aren't cloning we need to clean up the original entry
      if (!copy && draggingEl && !draggingEl.attr(`readonly`)) {
        // suppress tracking on this since we already tracked it above
        draggingEl.updateEntries(Action.Remove, {
          index: draggingEl.getEntries().findIndex((target) => target.entryKey === draggingEntry.entryKey),
          suppressTracking: true,
        });
      }
    }

    draggingEl.helpers.onEntryStepDragClear();
    this.helpers.autoactivatePlaceholders(true);
  }

  /**
   * Generates a new entry with a default operator applied
   */
  public getNewEntry(
    type: SelectorType,
    {
      aggregationOperator,
      event,
      dateRange,
    }: {
      event?: EntryItem;
      aggregationOperator?: MATH_TYPE;
      dateRange?: Partial<DateRange>;
    } = {},
  ): QuerySuperEntry {
    const placeHolderEntry = this.getPlaceHolderEventEntry(type);
    const isDateRangeType = this.helpers.isDateRangeType(type);

    // TODO(pablo): move all of these QuerySuperEntry handlers
    // to different classes to avoid generics in this class
    const emptyEntry = getNewEmptyEntry(type);
    if (emptyEntry) {
      return emptyEntry;
    }

    return {
      ...placeHolderEntry,
      ...(this.attr(`data-group-id`) ? {dataGroupId: this.attr(`data-group-id`)} : null),
      ...(isDateRangeType ? {dateRange: dateRange ?? this.helpers.getDefaultDateValue()} : {}),
      aggregationOperator: aggregationOperator ?? this.getEntryDefaultOperator(type),
      aggregationOperatorPerUser: null,
      event: event || placeHolderEntry.event,

      // TODO: User entry support is limited to one selection, this could
      // change so that an option per data group is available
      ...(isUserBasedType(placeHolderEntry) ? {readOnlyEvent: true} : {}),
    } as QuerySuperEntry;
  }

  public getEntryDefaultOperator(type: SelectorType) {
    // TODO: Return null for filter/segment selector types
    if (type === ResourceType.Cohort) return PRIMITIVE_MATH_TYPES.UNIQUE;
    // Default count type for new count menu is "Unique"
    // This part can be removed once we no longer need hasNewCountMenu flag
    return this.hasNewCountMenu() ? PRIMITIVE_MATH_TYPES.UNIQUE : PRIMITIVE_MATH_TYPES.TOTAL;
  }

  /**
   * Returns an aggregator function for a given selector type aggregation
   */
  public getSelectorTypeDefaultAggregator(type: SelectorTypeAggregation): MATH_TYPE {
    return type.subAggregations ? type.subAggregations[0].operator : type.operator;
  }

  /**
   * Returns an empty entry for a given source
   */
  public getPlaceHolderEventEntry(type: SelectorType): QueryEventEntry | QueryPropertyEntry {
    const forExclusionStep = this.attr(`entry-type`) === EntryType.ExclusionEvent;

    const event = null;
    if (!isPropertySelectorType(type)) {
      const label = this.attr(`add-entry-label`) || `Select ${capitalize(SelectorTypesLabels[type])}`;
      return {
        ...(forExclusionStep ? EMPTY_EXCLUSION_EVENT_ENTRY : EMPTY_EVENT_ENTRY),
        event: {
          ...EMPTY_EVENT_ENTRY.event,
          label,
        },
        type,
      } as QueryEventEntry;
    }

    return {
      // TODO: Don't set EMPTY_EVENT_ENTRY fields for segment/property selector types
      ...(forExclusionStep ? EMPTY_EXCLUSION_EVENT_ENTRY : EMPTY_EVENT_ENTRY),
      type,
      event,
    };
  }

  public getSelectedPropertyForEntry(entry: QuerySuperEntry) {
    return entry && entry.property;
  }

  public getSelectedAggregationData({
    entry,
    forNewWorkflow = false,
  }: {
    entry: QueryMetricEntry;
    forNewWorkflow?: boolean;
  }) {
    return getSelectedAggregationData({
      entry,
      dataGroupId: this.attr(`data-group-id`),
      dataGroups: this.state.dataGroups,
      forNewWorkflow,
    });
  }

  public getSelectedAggregation(entry: QueryMetricEntry): CountBy {
    const {aggregationOperator, aggregationOperatorPerUser, property} = entry;
    let countType = getCountTypeForEntry(entry);
    const advancedMenuValue: Calculation = {
      calculationType: null,
    };
    if (XAU_ONLY_MATH_TYPES.includes(aggregationOperator as XAU_MATH_TYPES)) {
      advancedMenuValue.calculationType = CalculationTypes.XAU;
      advancedMenuValue[`xauOperator`] = aggregationOperator as XAU_MATH_TYPES;
      countType = InsightsCountType.UniqueUsers;
    } else if (entry.rolling) {
      advancedMenuValue.calculationType = CalculationTypes.Rolling;
      advancedMenuValue[`rollingWindowSize`] = entry.rolling.rollingWindowSize;
    } else if (entry.cumulative) {
      advancedMenuValue.calculationType = CalculationTypes.Cumulative;
    }

    const countBy: CountBy = {countType, aggregationOperator, aggregationOperatorPerUser, property, advancedMenuValue};

    return countBy;
  }

  /**
   * Filters aggregation functions to match the provided selector types and optionally defined operators
   */
  public filterSelectorTypeAggregationSections(
    filters: {[key in SelectorType]?: Array<{operator: MATH_TYPE; forProperty: boolean}>},
    aggregationItems: SelectorTypeAggregation[],
    isBehaviorSelectorType: boolean,
  ): Array<Section<SelectorTypeAggregation>> {
    const matchesType = (aggregationItem: SelectorTypeAggregation) => {
      return Object.entries(filters).some(([filterType, filterValues]) => {
        const matchesSelectorType = AggregationsMappings.get(filterType as SelectorType)?.has(aggregationItem);

        let matchesOperator = filterValues
          ? filterValues.some(
              (value) =>
                value.operator === aggregationItem.operator &&
                (aggregationItem.forProperty || false) === value.forProperty,
            )
          : true;

        /** @todo [pablo] provide behaviors with a specific list of aggregations */
        matchesOperator =
          (aggregationItem.operator === PRIMITIVE_MATH_TYPES.MOST_FREQUENT ||
            aggregationItem.operator === PRIMITIVE_MATH_TYPES.FIRST_VALUE) &&
          !isBehaviorSelectorType
            ? false
            : matchesOperator;

        return matchesSelectorType && matchesOperator;
      });
    };

    const sectionItems: Array<SectionItem<SelectorTypeAggregation>> = aggregationItems
      .filter((aggregationItem) => {
        // filter out nested menu options that do not match filters
        if (Array.isArray(aggregationItem.subSections)) {
          return aggregationItem.subSections.some(matchesType);
        }
        return matchesType(aggregationItem);
      })
      .map((aggregationItem) => ({
        ...this.generateAggregationSectionItem(aggregationItem),
        subSections: aggregationItem.subSections
          ? this.filterSelectorTypeAggregationSections(filters, aggregationItem.subSections, isBehaviorSelectorType)
          : null,
      }));

    return sectionItems.length ? [{items: sectionItems}] : [];
  }

  /**
   * Returns aggregation functions for all supported row types
   */
  public getSelectorAggregationsForEntry(): Array<Section<SelectorTypeAggregation>> {
    const listTypes = this.getAddListTypeItems();

    /** @todo [pablo] remove behavior specific code from main component */
    let isBehaviorSelectorType = false;

    const filters = mapValues(listTypes, (rowTypeOptions, rowType) => {
      let operatorFilters = null;

      if (rowType === ResourceType.Event) {
        const aggregationFilters = this.attr(`event-aggregator-functions`)?.map((operator) => ({
          operator,
          forProperty: false,
        }));
        const propertyAggregationFilters = this.attr(`property-aggregator-functions`)?.map((operator) => ({
          operator,
          forProperty: true,
        }));
        operatorFilters =
          aggregationFilters || propertyAggregationFilters
            ? [...aggregationFilters, ...propertyAggregationFilters]
            : null;
      }

      // currently only distinguishing property between behaviorSelectorType and regular selector types is the includeDateRange field
      // listTypes currently only has either all behaviorSelectorType or no behaviorSelectorTypes

      isBehaviorSelectorType = isBehaviorSelectorType || rowTypeOptions?.includeDateRange;

      return operatorFilters;
    });

    return this.filterSelectorTypeAggregationSections(filters, NESTED_AGGREGATIONS_MENU_ITEMS, isBehaviorSelectorType);
  }

  /**
   * Checks if a given entry is not commited to the list
   */
  public isStagedEntry(index: number) {
    return index >= this.state.stagedRows.length;
  }

  /**
   * Checks if a given entry is an empty block with no event/property selected
   */
  public isEmptyPlaceholder(index: number, entry: QuerySuperEntry) {
    const entryRow = this.createEntryRow(index, entry);
    return entryRow.isPlaceholder() && !entryRow.isIntermediatePlaceholder();
  }

  /**
   * Retrieve an entry from the list.
   *
   * Return type is overridable because the type combinations can't be expressed
   * at this generic level.
   */
  public getEntry<T extends QuerySuperEntry>(index: number): T {
    return this.getEntries({appendPlaceholder: true})[index] as T;
  }

  public getRows(appendPlaceholder = false): EntryRow[] {
    const {stagedRow, stagedRows} = this.state;

    return [
      ...stagedRows,
      // placeholder row
      ...(appendPlaceholder && stagedRow ? [stagedRow] : []),
    ];
  }

  public getEntries({appendPlaceholder = false}: {appendPlaceholder?: boolean} = {}): QuerySuperEntry[] {
    return this.getRows(appendPlaceholder).map((row) => row.entry);
  }

  /** callback for `Add grouped event` line action */
  addOrConvertToGroupedEvent(index: number) {
    if (this.attr(`group-event-behavior`) === GroupEventBehaviors.Append) {
      this.updateEntries(Action.Add, {index: index + 1, entry: this.getPlaceHolderEventEntry(ResourceType.Event)});
    } else if (this.attr(`group-event-behavior`) === GroupEventBehaviors.Nest) {
      this.updateEntries(Action.Group, {
        index,
      });
    } else {
      throw new Error(`Somehow doing group stuff with no behavior defined: ${this.attr(`group-event-behavior`)}`);
    }
  }

  set prefixRenderer(prefixRenderer) {
    this.update({prefixRenderer});
  }

  set suffixRenderer(suffixRenderer) {
    this.update({suffixRenderer});
  }

  /**
   * Performs one of Action operations on the staged entries
   */
  public updateEntries(
    action: Action,
    {
      entry,
      index,
      trackingProps,
      queryReason,
      suppressTracking = false,
    }: {
      entry?: QuerySuperEntry;
      index: number;
      trackingProps?: Obj;
      queryReason?: QueryReason;
      suppressTracking?: boolean;
    },
  ): UpdateStatus {
    let entries = this.getEntries();
    entry = entry ?? entries[index];

    if (this.isStagedEntry(index) && action === Action.Edit) {
      // when a staged entry row is ready to be commited, we override
      // the action to commit the staged entry
      if (this.shouldCommitUpdate(action, entry, index)) {
        action = Action.Add;
      } else {
        this.update({
          stagedRow: this.createEntryRow(index, {...entry, entryKey: this.nextEntryKey++}),
        });
        return {dispatchedUpdate: false};
      }
    } else if (entry && this.shouldCommitUpdate(action, entry, index)) {
      entry.persistedEntry = null;
    } else if (action === Action.Edit) {
      // for any action that results in changing from a commited entry to a staged one
      // we persist the commited state in persistedEntry so that all commited entries
      // are preserved when another change occurs
      const sourceEntry = this.getEntry(index);
      const isStagingChanges = this.isStagingEntryChanges(sourceEntry, entry);
      entry.persistedEntry = isStagingChanges ? sourceEntry : null;
    }

    switch (action) {
      case Action.Add:
        entries = [...entries, {...entry, entryKey: this.nextEntryKey++}];
        break;
      case Action.Clone:
        entry = cloneDeep(entry);
        if (!isPropertyEntry(entry)) {
          entry.event = omit(entry.event, `renamed`) as EventItem | SavedCohortItem;
        }
        entries = insertAtIndex(entries, index + 1, {...entry, entryKey: this.nextEntryKey++});
        break;
      case Action.Ungroup:
      case Action.Edit:
      case Action.EditFlowSteps:
        entries = replaceByIndex(entries, index, {...entries[index], ...entry});
        break;
      case Action.Remove:
        entry = entries[index];
        if (entry) entries = removeByIndex(entries, index);
        break;
      case Action.Group:
        entry = cloneDeep(entries[index]);
        entries = replaceByIndex(entries, index, {
          filters: [],
          type: SelectorTypeDefinitions.GroupedEvent,
          event: [entry, this.getPlaceHolderEventEntry(ResourceType.Event)],
        });
        break;
    }

    return this.commitAndTrackUpdate(entries, entry, {queryReason, action, index, trackingProps, suppressTracking});
  }

  private shouldCommitUpdate(action: Action, entry: QuerySuperEntry, index: number): boolean {
    if (action === Action.Group) return false;
    if ([Action.Remove, Action.Clone].includes(action)) return true;
    return entry && !this.createEntryRow(index, entry).isPlaceholder();
  }

  private commitAndTrackUpdate(
    entries: QuerySuperEntry[],
    entry: QuerySuperEntry,
    {
      index,
      queryReason = QueryReason.QbOtherUpdate,
      action,
      trackingProps,
      suppressTracking = false,
    }: Partial<{
      index: number;
      queryReason: QueryReason;
      action: Action;
      trackingProps: Obj;
      suppressTracking?: boolean;
    }>,
  ): UpdateStatus {
    const updateStatus: UpdateStatus = {dispatchedUpdate: false};

    // If the number of entries is less than the mininum number of the required entries,
    // fill in the missing blocks with an empty placeholder entry
    let stagedRows = entries.map((entry, index) => this.createEntryRow(index, entry));
    if (this.attr(`min-entries`) && entries.length < this.attr(`min-entries`)) {
      const defaultEmptyEntry = this.attr(`empty-placeholder`);
      stagedRows = [...this.state.stagedRows];
      if (defaultEmptyEntry) {
        stagedRows[index] = this.createEntryRow(index, defaultEmptyEntry);
      } else {
        stagedRows[index] = this.createEntryRow(index, entry);
      }
    }

    this.update({
      hasOpenDropdown: false,
      stagedRows,
      // make sure the currently staged row index is refreshed
      ...(this.state.stagedRow ? {stagedRow: this.createEntryRow(entries.length, this.state.stagedRow.entry)} : {}),
    });

    // TODO(pablo): avoid managing placeholder entries at the report level
    // as a first iteration, the ideal behavior is reports should not query
    // for empty rows but this behavior has added complexity for formulas
    // in Insights, exclusion steps and attribution in Funnels, as a side effect
    // any staged changes from rows other than the commited change or the staged
    // row will be lost on the next update to the entries attribute
    const shouldCommit = this.shouldCommitUpdate(action, entry, index);
    if (shouldCommit) {
      captureWorkflowTiming(this, {
        key: `query-update`,
        name: `QB Update to Query Start`,
        isComplete: queryStartedWorkflowComplete,
      });
      const committedEntries: QuerySuperEntry[] = entries.map((entry) => {
        return {
          ...omit(entry.persistedEntry ?? entry, [`persistedEntry`, `entryKey`]),
          type: entry.type,
        } as QuerySuperEntry;
      });

      this.setActiveEntry(null, null);
      this.pendingQueryReason = maxQueryReason([this.pendingQueryReason, queryReason]);
      this.dispatchUpdate(
        new CustomEvent(`update`, {detail: {entries: committedEntries, updatedEntry: entry, updatedEntryIndex: index}}),
      );
      updateStatus.dispatchedUpdate = true;

      // abort current staged row when committing an entry
      if (action === Action.Add) {
        this.abortRowStage();
      }
      this.helpers.autoactivatePlaceholders();
    }

    // behavioral filters are currently being transformed from PropertyFilter[] into QueryBehaviorEntry[] which
    // uses an `event` entry selector type, these interactions are tracked as `Add event`, `Remove event`, etc..
    // until we can define these purely with a `behavior` entry selector type, we transform the type for tracking purposes
    // entry.type = SelectorTypeDefinitions.Behavior;
    /** @todo [pablo] define behaviors and behavioral filters with pure `behavior` selector types */
    if (entry && (entry as QueryBehaviorEntry).dateRange && isEventBasedType(entry.type)) {
      const behaviorEntry = {...entry, type: SelectorTypeDefinitions.Behavior} as QueryBehaviorEntry;
      this.trackAction(index, behaviorEntry, action, {
        ...trackingProps,
        ...getBehaviorEntryTrackingProps(behaviorEntry),
      });
    } else if ((!suppressTracking && action !== Action.Edit) || (entry && isPropertyEntry(entry))) {
      // edit actions are tracked with more detailed elsewhere
      this.trackAction(index, entry, action, trackingProps);
    }

    // Chameleon Tour for Custom Breakdowns - TODO remove in a couple months
    if (
      action === Action.Add &&
      isSegmentEntry(entry) &&
      shouldCommit &&
      this.canCustomizeBuckets(index) &&
      this.usingNewBucketing(index) &&
      currentProjectStore.value &&
      !currentProjectStore.value.hasFeatureFlag(ProjectFeatureFlag.ValueComparison)
    ) {
      if (currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.ValueComparisonLegacy)) {
        showTour(TourId.CustomizeGroupsLegacy, true);
      } else {
        showTour(TourId.CustomizeGroups, true);
      }
    }

    return updateStatus;
  }

  /**
   * Removes an entry.
   */
  public removeEntry(index: number) {
    if (this.isStagedEntry(index)) {
      this.abortRowStage();
    } else {
      this.updateEntries(Action.Remove, {index});
    }
  }

  public dispatchTrack(eventName: string, props: Obj): void {
    const properties = {
      ...props,
      ...this.attr(`track-properties`),
      featureFlags: Array.from(this.state.featureFlags).concat(
        FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(
          this.getContext(`currentProject`).get()?.value,
        )
          ? [ProjectFeatureFlag.InsightsNewCountMenu]
          : [],
      ),
      [`usage context`]: TrackingUsageContext.QueryBuilder,
    };
    emitTrackEvent(this, {eventName, props: properties, cancelable: true});
  }

  // TODO(pablo): find a better way to abstract this
  public getQueryBlockConfig(type: SelectorType, index?: number): Obj {
    switch (type) {
      case SelectorTypeDefinitions.PropertyExpansion:
        return {
          type: `breakdown`,
          readonly: this.attr(`readonly`),
          'exclude-autotrack-events': this.attr(`exclude-autotrack-events`),
          'exclude-dimension-properties': this.attr(`exclude-dimension-properties`),
          'include-custom-properties': this.attr(`include-custom-properties`),
        };
      case ResourceType.Event:
      case SelectorTypeDefinitions.FlowsEvent:
        return {
          type: `events`,
          'all-data-groups': this.attr(`data-groups-enabled`),
          readonly: this.attr(`readonly`),
          'exclude-autotrack-events': this.attr(`exclude-autotrack-events`),
          'exclude-dimension-properties': this.attr(`exclude-dimension-properties`),
          'include-custom-properties': this.attr(`include-custom-properties`),
          'is-blocked-people': this.attr(`is-blocked-people`),
          'project-has-people': this.attr(`project-has-people`),
          'project-has-cohorts': this.attr(`project-has-cohorts`) && this.attr(`allow-cohort-filter`),
          'show-all-events': this.attr(`show-all-events`),
          'show-any-event': this.attr(`show-any-event`),
          'show-session-event': this.attr(`show-sessions`),
          'show-top-events': this.attr(`restrict-top-events-only`)
            ? this.attr(`show-top-events`) && index === 0 && this.state.stagedRows.length < 2
            : this.attr(`show-top-events`),
          'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
        };
      case ResourceType.Cohort:
        return {
          type: `properties`,
          'include-custom-properties': this.attr(`include-custom-properties`),
          'project-has-cohorts': true,
          'hide-events': true,
          'project-has-people': false,
          'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
          'inline-cohorts-override': this.attr(`inline-cohorts-override`),
        };
      case ResourceType.User:
        return {
          type: `properties`,
          'exclude-dimension-properties': this.attr(`exclude-dimension-properties`),
          'include-custom-properties': this.attr(`include-custom-properties`),
          'project-has-cohorts': false,
          'hide-events': true,
          'project-has-people': true,
        };
      case SelectorTypeDefinitions.Filter:
      case SelectorTypeDefinitions.Segment: {
        let config: Obj = {
          'cohort-multiselect': true,
          'all-data-groups': this.attr(`data-groups-enabled`),
          'event-names': this.attr(`event-names`),
          'exclude-dimension-properties': this.attr(`exclude-dimension-properties`),
          'include-custom-properties': this.attr(`include-custom-properties`),
          'project-has-cohorts': this.attr(`project-has-cohorts`),
          'hide-events': this.attr(`hide-events`),
          'project-has-people': this.attr(`project-has-people`),
          'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
          'hide-edit-controls': this.attr(`hide-edit-controls`),
          'property-combine-mode': this.attr(`property-combine-mode`) ?? undefined,
        };
        if (type === SelectorTypeDefinitions.Filter) {
          config = {
            ...config,
            type: `filter`,
            'other-filter-options': this.attr(`other-filter-options`),
          };
        } else {
          config = {
            ...config,
            type: `properties`,
            'show-all-tab': true,
            'other-group-by-options': this.attr(`other-group-by-options`),
          };
        }
        return config;
      }
      case SelectorTypeDefinitions.Behavior:
        return {
          type: `events`,
          'include-custom-properties': this.attr(`include-custom-properties`),
          'hide-events': false,
          'project-has-people': true,
          'show-all-events': this.attr(`show-all-events`),
          'show-any-event': this.attr(`show-any-event`),
          'show-session-event': this.attr(`show-sessions`),
          'show-top-events': this.attr(`show-top-events`),
        };
      case SelectorTypeDefinitions.EventLaunch:
        return {
          type: `events`,
          'exclude-autotrack-events': this.attr(`exclude-autotrack-events`),
          'include-custom-properties': this.attr(`include-custom-properties`),
          'is-blocked-people': this.attr(`is-blocked-people`),
          'project-has-people': this.attr(`project-has-people`),
          'show-all-events': this.attr(`show-all-events`),
          'show-any-event': this.attr(`show-any-event`),
          'show-session-event': this.attr(`show-sessions`),
          'show-top-events': this.attr(`show-top-events`),
        };
    }

    return {};
  }

  private _dispatchUpdate(ev) {
    this.dispatchEvent(ev);
  }

  private abortRowStage() {
    this.setActiveEntry(null, null);
    this.update({hasOpenDropdown: false, stagedRow: null});
    this.dispatchEvent(new CustomEvent(`abortStage`));
  }

  setActiveEntry(index: number | null, part: EntryPart | null) {
    this.update({openEntry: index !== null ? {index, part} : {}});
  }

  private isTypeOptionComplex(option: any): option is RowTypeComplexOption {
    return isObject(option);
  }

  /**
   * Generates the type of items that can be added to the list
   * eg. Event or Cohort
   */
  private getAddListTypeItems(): TypeList {
    const sections = {};
    const types = this.attr(`row-types`);

    // populate selected items
    for (const type of types) {
      const selectorType = this.isTypeOptionComplex(type) ? type.value : type;

      if (this.isTypeOptionComplex(type)) {
        sections[selectorType] = {
          ...LIST_TYPES[selectorType],
          ...type,
          value: selectorType,
        };
      } else {
        sections[selectorType] = LIST_TYPES[selectorType] ?? {value: selectorType};
      }
    }

    const transforms = {
      [ResourceType.Event]: (item) => {
        return {
          ...item,
          ...(this.attr(`add-entry-label`) ? {label: this.attr(`add-entry-label`)} : {}),
        };
      },
    };
    return mapValues(sections, (value, key) => (transforms[key] ? transforms[key](value) : value));
  }

  private getTrackingEvent(type: ResourceTrackingTypes, action: Action): string {
    const forExclusionStep = this.attr(`entry-type`) === EntryType.ExclusionEvent;

    if (forExclusionStep) {
      type = EntryType.ExclusionEvent;
    }

    return ResourceTrackingEvent[type][action];
  }

  /**
   * Updates aggregation function on an entry
   */
  public onEntryAggregationFunctionSelected({
    index,
    aggregation,
    isSubAggregation = false,
  }: {
    index: number;
    aggregation: SelectorTypeAggregation;
    isSubAggregation: boolean;
  }) {
    const sourceEntry = this.getEntry<QueryMetricEntry>(index);

    // remove property selection when changing to a non-aggregation type
    const keepProperty = aggregation.forProperty;

    // attempts to preserve the current aggregationOperator if it's available on the selected aggregation
    // prevent `Distinct Count by Sum` from transitioning
    const transitionToUniqueValuesPerUser =
      aggregation.perUserOperator === PRIMITIVE_MATH_TYPES.UNIQUE_VALUES &&
      sourceEntry.aggregationOperator === PRIMITIVE_MATH_TYPES.TOTAL;
    const keepAggregationOperator =
      !transitionToUniqueValuesPerUser && !isSubAggregation && aggregation.subAggregations
        ? aggregation.subAggregations.some(
            (subAggregation) => subAggregation.operator === sourceEntry.aggregationOperator,
          )
        : false;

    const entry: QueryMetricEntry = {
      ...sourceEntry,
      // remove property selection when changing to a non-aggregation type
      property: keepProperty ? sourceEntry.property ?? ({value: null} as PropertySectionItem) : null,

      aggregationOperator: keepAggregationOperator
        ? sourceEntry.aggregationOperator
        : this.getSelectorTypeDefaultAggregator(aggregation),

      aggregationOperatorPerUser: aggregation.perUserOperator || null,
    };

    this.updateEntries(Action.Edit, {entry, index});

    // focus on numeric property field when selecting a numeric property aggregation
    if (Boolean(entry.event?.value) && aggregation.forProperty && entry.property?.value === null) {
      this.update({openEntry: {index, part: EntryPart.NumericProperty}});
    }

    this.dispatchTrack(TrackingEvent.SelectNumericAggregator, {
      ...this.getEntryTrackingProps(index, entry),
      Aggregator: entry.aggregationOperator,
      'Per-user aggregator': entry.aggregationOperatorPerUser,
    });
  }

  isStagingEntryChanges(entry: QuerySuperEntry, updatedEntry: QuerySuperEntry) {
    const entryRow = this.createEntryRow(entry.entryKey, entry);
    const updatedEntryRow = this.createEntryRow(updatedEntry.entryKey, updatedEntry);

    return !entryRow.isPlaceholder() && updatedEntryRow.isPlaceholder();
  }

  /**
   * Updates event selection on an entry
   */
  onEntryEventSelected(
    index: number,
    value: EntryItem,
    {trackingProps, queryReason}: {trackingProps?: Obj; queryReason?: QueryReason},
  ) {
    const sourceEntry = this.getEntry(index);
    // TODO: This implies that every non-property is an event. Confirm this and implement strong type asserting.
    const event =
      isPropertyEntry(sourceEntry) || sourceEntry.type === ResourceType.Cohort
        ? value
        : filterToBookmarkableEventItem(value as EventItem);
    const entry = {...sourceEntry, event} as QuerySuperEntry;
    const row = this.createEntryRow(index, entry);

    const isPlaceholder = row.isPlaceholder();
    let action;
    if (this.isStagedEntry(index) && !isPlaceholder) {
      action = Action.Add;
    } else {
      action = Action.Edit;

      if (isSegmentEntry(entry) && entry.customBucket) {
        const existingProperty = this.getSegmentationPropertyByIndex(index);
        const updatedProperty = this.getSegmentationProperty(entry);
        if (
          updatedProperty.propertyType !== PropertyType.Number ||
          !isSamePropertyReference(existingProperty, updatedProperty)
        ) {
          entry.customBucket = null;
        }
      } else {
        // removes invalid first time filters
        const updatedRow = this.createEntryRow(index, entry);
        const rowFilteringCapabilities = this.getRowFilteringCapabilities(updatedRow);
        if (!rowFilteringCapabilities.canApplyFirstTimeEventFilter) {
          entry.filters = entry.filters.filter((filter) => !isFirstTimeEventFilter(filter));
        }
      }
    }

    this.updateEntries(action, {entry, index, trackingProps, queryReason});

    this.update({openEntry: isPlaceholder ? {index, part: EntryPart.NumericProperty} : {}});
  }

  /**
   * Updates aggregation selection on an entry

   * @param index row to modify
   * @param countBy selected aggregation item
   */
  handleCountBySelected(index: number, countBy: CountBy, unit: RollingWindowSize) {
    const {aggregationOperatorPerUser = null, property = null, advancedMenuValue = null} = countBy;
    let newAggregationOperator = countBy.aggregationOperator;
    const originalEntry = this.getEntry<QueryMetricEntry>(index);
    let rolling = originalEntry.rolling;
    let cumulative = originalEntry.cumulative;
    if (advancedMenuValue && isAdvancedValueCalculation(advancedMenuValue)) {
      // commited from advanced menu (changed calculation type)
      const calculationType = advancedMenuValue.calculationType;
      if (calculationType === CalculationTypes.Rolling) {
        rolling = {rollingWindowSize: advancedMenuValue.rollingWindowSize};
        cumulative = false;
      } else if (calculationType === CalculationTypes.XAU) {
        newAggregationOperator = advancedMenuValue.xauOperator;
        rolling = null;
        cumulative = false;
      } else if (calculationType === CalculationTypes.Cumulative) {
        rolling = null;
        cumulative = true;
        if (isXauMathType(originalEntry.aggregationOperator)) newAggregationOperator = PRIMITIVE_MATH_TYPES.UNIQUE;
      }
    } else {
      // commited from count menu (changed count type)
      // When Rolling calculation is applied, changing count type to Unique sets aggregationOperator to default XAU (DAU)
      // When XAU is selected, changing count type to count types other than Unique sets rolling window size to default size
      // When XAU is selected, selecting Unique should not change aggregationOperator to "unique". It will remain as "xau"
      if (rolling && newAggregationOperator === ALL_MATH_TYPES.UNIQUE) {
        newAggregationOperator = XAU_MATH_TYPES.DAU;
        rolling = null;
        cumulative = false;
      } else if (isXauMathType(originalEntry.aggregationOperator)) {
        if (newAggregationOperator !== ALL_MATH_TYPES.UNIQUE) {
          rolling = {rollingWindowSize: defaultRollingWindowSize(unit)};
          cumulative = false;
        } else {
          newAggregationOperator = originalEntry.aggregationOperator;
        }
      }
    }

    const entry: QueryEntry = {
      ...originalEntry,
      aggregationOperator: newAggregationOperator,
      aggregationOperatorPerUser,
      property,
      rolling,
      cumulative: Boolean(cumulative),
    };
    this.updateEntries(Action.Edit, {entry, index});
    this.dispatchTrack(TrackingEvent.SelectNumericAggregator, {
      ...this.getEntryTrackingProps(index, entry),
      Aggregator: newAggregationOperator,
      'Per-user aggregator': aggregationOperatorPerUser,
      'Rolling window size': rolling?.rollingWindowSize,
      Cumulative: Boolean(cumulative),
      ...(property && {
        'Property name': property.label,
        'Property data type': `type` in property && property.type,
      }),
    });
  }

  handleCalculationSelected(index: number, calculation: Calculation) {
    const originalEntry = this.getEntry<QueryMetricEntry>(index);
    let aggregationOperator = originalEntry.aggregationOperator;
    let rolling = originalEntry.rolling;
    let cumulative = Boolean(originalEntry.cumulative);
    if (calculation.calculationType === CalculationTypes.Rolling) {
      rolling = {rollingWindowSize: calculation.rollingWindowSize};
      cumulative = false;
    } else if (calculation?.calculationType === CalculationTypes.XAU) {
      aggregationOperator = calculation.xauOperator;
      rolling = null;
      cumulative = false;
    } else if (calculation.calculationType === CalculationTypes.Cumulative) {
      rolling = null;
      cumulative = true;
      if (isXauMathType(aggregationOperator)) aggregationOperator = PRIMITIVE_MATH_TYPES.UNIQUE;
    }

    const entry: QueryEntry = {
      ...originalEntry,
      aggregationOperator,
      rolling,
      cumulative,
    };
    this.updateEntries(Action.Edit, {entry, index});
  }

  handleCalculationRemoved(index: number) {
    const originalEntry = this.getEntry<QueryMetricEntry>(index);
    let aggregationOperator = originalEntry.aggregationOperator;
    if (XAU_ONLY_MATH_TYPES.includes(originalEntry.aggregationOperator as XAU_MATH_TYPES)) {
      aggregationOperator = ALL_MATH_TYPES.UNIQUE;
    }

    const entry: QueryEntry = {
      ...originalEntry,
      aggregationOperator,
      rolling: null,
      cumulative: null,
    };
    this.update({hoveredEntry: {index: null, part: null}});
    this.updateEntries(Action.Edit, {entry, index});
  }

  handleCalculationMouseEnter(index: number) {
    this.update({hoveredEntry: {index, part: EntryPart.Calculation}});
  }

  handleCalculationMouseLeave() {
    this.update({hoveredEntry: {index: null, part: null}});
  }

  /**
   * Formats a given label to include the selected data group (Users, Accounts, etc...)
   */
  private formatDataGroupLabel(label: SelectorTypeAggregationLabel) {
    return formatDataGroupLabel({label, dataGroupId: this.attr(`data-group-id`), dataGroups: this.state.dataGroups});
  }

  public getSelectedGroupLabel(isPlural = false) {
    const dataGroupId = this.attr(`data-group-id`);
    return getSelectedGroupLabel({dataGroupId, dataGroups: this.state.dataGroups, isPlural});
  }

  private async fetchDataGroups() {
    const projectId = this.attr(`project-id`);
    const workspaceId = this.attr(`workspace-id`);
    const paramsUnchanged = () => projectId === this.attr(`project-id`) && workspaceId === this.attr(`workspace-id`);

    if (this.attr(`data-groups-enabled`)) {
      const dataGroups = await dataGroupsClient.query({projectId, workspaceId});
      if (paramsUnchanged()) {
        this.update({dataGroups: [DEFAULT_DATA_GROUP, ...dataGroups]});
        this.updateStagedRowsFromEntries();
      }
    }
  }

  scrollNthRowIntoView(index) {
    if (index > 0 && this.shadowRoot) {
      const lastEntryRowEl = this.shadowRoot.querySelector(`.mp-query-entry-section-body :nth-child(${index})`);
      if (lastEntryRowEl) {
        window.requestAnimationFrame(() => lastEntryRowEl.scrollIntoView?.call(lastEntryRowEl, true));
      }
    }
  }

  /**
   * Stages or adds a new entry row for a given SelectorType or SelectorTypeAggregation
   */
  addEntryRow(type: SelectorType, {typeSelect = false}: {typeSelect?: boolean} = {}) {
    const index = this.helpers.getEntries().length;

    // checks for static defined row types and append that to the list
    const typeDefinition = this.getAddListTypeItems()[type];
    const baseEntry = typeDefinition.placeholderEntry ?? {};
    const entry = Object.assign(this.getNewEntry(type, {event: baseEntry?.event}), baseEntry);
    const entryRow = this.createEntryRow(0, entry);
    this.dispatchTrack(TrackingEvent.AddEntryRow, {type});

    // If selecting a row type when there is only an empty placeholder (intial empty state), replace the existing entry row
    // with a newly selected row type and open a placeholder block instead of adding a new entry
    if (typeSelect && this.state.stagedRows.length === 1 && this.isEmptyPlaceholder(0, this.getEntry(0))) {
      this.update({
        openEntry: {index: 0, part: EntryPart.Value},
        stagedRows: [entryRow],
      });
      return;
    }

    if (entryRow.isPlaceholder()) {
      this.isStagingRow = Object.assign(this.getNewEntry(type), baseEntry);
    } else {
      this.updateEntries(Action.Add, {entry, index});
    }
  }

  private createEntryRow(index: number, entry: QuerySuperEntry) {
    return createEntryRow(entry.type, {index, entry, section: this}, {});
  }

  /**
   * Prompts for a custom label on a given entry index
   */
  public async promptRename(index: number) {
    let entry = this.getEntry<QueryEventEntry | QueryFilterEntry>(index);
    const {event: stagedEvent} = entry;
    let label, isCustom;
    const isFilter = isFilterEntry(entry);
    if (isFilter) {
      label = new PropertyFilter(entry.event).getValuesLabel(false);
    } else {
      const event = stagedEvent as EventItem;
      isCustom = event.custom;
      label = (stagedEvent as EventItem).label;
    }

    const inputValidator = (stagedInput) => {
      const trimmed = stagedInput.trim();
      const isValid = this.getEntries().every((entry: QueryEventEntry) => {
        return trimmed === label || (trimmed !== entry.event.label && trimmed !== entry.event.renamed);
      });
      return isValid ? null : `Name is already used.`;
    };

    const promptResponse = await prompt(
      {
        title: `Rename`,
        'initial-input-value': stagedEvent.renamed || label,
        'allow-empty-input-value': true,
        'confirm-button-label': `Save`,
        'show-input': true,
      },
      {inputValidator},
    );

    if (promptResponse.didConfirm) {
      const newLabel = promptResponse.inputValue;
      const hasLabel = label !== newLabel && newLabel !== ``;
      entry = {
        ...entry,
        event: {
          ...omit(stagedEvent, [`renamed`]),
          ...{renamed: hasLabel ? newLabel : null},
        },
      } as QueryFilterEntry | QueryEventEntry;
      this.updateEntries(Action.Edit, {entry, index});
      this.dispatchTrack(TrackingEvent.RenameEvent, {
        ...this.getEntryTrackingProps(index, entry),
        'is custom event': isCustom,
        'is customized group': isFilter,
        'Renamed name': newLabel,
        'is removing rename': !hasLabel,
      });
    }
  }

  private getPropertyType(index: number) {
    const row = this.getRows(true)[index];
    if (isFilterEntry(row.entry) || isSegmentEntry(row.entry)) {
      return (row as FilterEntryRow | SegmentEntryRow).getPropertyType();
    }
    return null;
  }

  private doTypecast(index: number, toType: FullPropertyType) {
    const row = this.getRows(true)[index];
    return (row as FilterEntryRow | SegmentEntryRow).setPropertyType(toType);
  }

  getSegmentationProperty(entry: QuerySegmentEntry): Segmentation | null {
    let segment = entry.event;

    // can be an array when customizing segments
    if (Array.isArray(segment) && segment.length && !isCohortSegment(segment)) {
      segment = entry.event[0] as SegmentValue;
    }

    if (isCohortSegment(segment) || Array.isArray(segment) || segment.resourceType === ResourceType.Other) {
      return null;
    }

    return segment;
  }

  getSegmentationPropertyByIndex(index): Segmentation {
    return this.getSegmentationProperty(this.getEntry(index));
  }

  trackAction(index: number, entry: QuerySuperEntry, action: Action, trackingProps?: Obj) {
    /** @todo [pablo] define behaviors and behavioral filters with pure `behavior` selector types */
    if (isEventBasedType(entry.type) && (entry as QueryBehaviorEntry).dateRange) {
      entry = {...entry, type: SelectorTypeDefinitions.Behavior} as QueryBehaviorEntry;
      trackingProps = {...trackingProps, ...getBehaviorEntryTrackingProps(entry)};
    }

    const consolidatedProps = {
      ...this.getEntryTrackingProps(index, entry),
      ...(trackingProps || {}),
      ...(entry && entry.type === ResourceType.Event ? {'is custom event': entry.event.custom} : {}),
      ...(entry.type === ResourceType.Cohort
        ? {
            'cohort type': isInlineCohortItem(entry.event) ? `inline cohorts` : `cohorts`,
          }
        : {}),
    };
    this.dispatchTrack(this.getTrackingEvent(entry.type, action), consolidatedProps);
  }

  getEntryTrackingProps(index: number, entryOverride?: QuerySuperEntry) {
    const entry = entryOverride || this.getEntry(index);
    return {
      'Entry index': index,
      'Entry type': entry.type,
    };
  }

  getCustomBucketTrackingProps(bucket: Bucket) {
    return {
      'Bucket min': bucket.min,
      'Bucket max': bucket.max,
      'Bucket size': bucket.bucketSize,
    };
  }

  updateCustomBucket(index: number, customBucket: Bucket) {
    const entry = this.getEntry(index);
    const updatedEntry = {...entry, customBucket};
    this.updateEntries(Action.Edit, {entry: updatedEntry, index});
  }

  getCustomBucketByIndex(index: number): Bucket {
    return this.getEntry<QueryBucketableEntry>(index)?.customBucket;
  }

  getCustomBucketText(index: number, isOpen: boolean) {
    const bucket = this.getCustomBucketByIndex(index);
    if (this.usingNewBucketing(index)) {
      const entry = this.getEntries()[index];
      const type = this.getPropertyType(index);
      const simpleType = (entry.event as Segmentation)?.listItemGroup?.propertyType || type.type;
      const bucketing = capitalize(CUSTOM_BUCKET_NAMING.bucketName);
      let bucketText, valid;

      switch (simpleType) {
        case PropertyType.Number:
          valid = !!bucket;
          bucketText = isOpen ? `Numeric ${bucketing}...` : valid && getBucketPreview(bucket);
          break;
        case PropertyType.String:
        case PropertyType.List:
        case PropertyType.Object: // TODO update if we ever want Objects to support other types
          valid = entry.event[`customGroups`]?.length > 0;

          // In theory the prop values could mess with the markdown but probably okay...
          // Looks like "Bucketing by **renamed; prop1, prop2; prop3**
          bucketText = isOpen
            ? `${bucketing} by...`
            : valid &&
              `${bucketing} by **` +
                entry.event[`customGroups`]
                  .map(
                    (group) =>
                      group.renamed ??
                      (Array.isArray(group.filterValue) ? group.filterValue.join(`, `) : group.filterValue),
                  )
                  .join(` | `) +
                `**`;
          break;
        case PropertyType.Datetime:
          valid = true;
          bucketText = `by **${TimeUnitLabels[entry.event[`unit`]] || `ISO 8601`}**`;
          break;
        default:
          return null;
      }
      return isOpen || valid ? bucketText : null;
    } else if (bucket) {
      return `Custom bucket size **${bucket.bucketSize} between ${bucket.min}-${bucket.max}**`;
    }
    return null;
  }

  usingNewBucketing(index: number) {
    const entry = this.getEntries()[index];
    return entry && isSegmentEntry(entry);
  }

  canCustomizeBuckets(index: number) {
    const entry = this.getEntry(index);
    let result = false;
    if (entry.type === SelectorTypeDefinitions.Segment) {
      const property = this.getSegmentationPropertyByIndex(index);
      const propertyType = property?.listItemGroup?.propertyType || property?.propertyType;
      result = this.attr(`can-customize-numeric-buckets`) && propertyType === PropertyType.Number;
      // TODO, add a separate "can-customize-string-buckets" prop or make this one more generic?
      result ||=
        this.attr(`can-customize-numeric-buckets`) &&
        this.usingNewBucketing(index) &&
        !isCohortSegment(entry.event) &&
        propertyType !== PropertyType.Boolean;
    } else if (entry.aggregationOperator === PRIMITIVE_MATH_TYPES.HISTOGRAM) {
      result = this.attr(`can-customize-numeric-buckets`);
    }
    return result;
  }

  onCustomizeBuckets(index: number, entryPoint: ControlSelectEntryPoint) {
    this.update({openEntry: {index, part: EntryPart.CustomBucketing}});
    const customBucket = this.getCustomBucketByIndex(index);
    const type = this.getPropertyType(index);
    const simpleType = type.isList ? PropertyType.List : type.type;
    this.dispatchTrack(TrackingEvent.OpenCustomBucketPane, {
      ...this.getEntryTrackingProps(index),
      ...(customBucket ? this.getCustomBucketTrackingProps(customBucket) : {}),
      'Is editing': !!customBucket,
      entryPoint,
      'Property Type': simpleType,
    });
  }

  getMultiAttribution(index: number): MultiAttribution {
    const entry = this.getEntry(index);
    return isEventEntry(entry) && entry.multiAttribution;
  }

  isMultiAttributionMenuOpen(index: number): boolean {
    const {index: activeIndex, part: activePart} = this.state.openEntry;
    return activeIndex === index && activePart === EntryPart.MultiAttribution;
  }

  getMultiAttributionTitle(index: number): string {
    const multiAttribution = this.getMultiAttribution(index);
    if (multiAttribution) {
      return AttributionTypeToSpec[multiAttribution.type].description;
    }
    return ``;
  }

  getMultiAttributionLabel(index: number): string {
    const multiAttribution = this.getMultiAttribution(index);
    if (multiAttribution) {
      return multiAttribution.type === MultiAttributionType.Custom
        ? multiAttribution.name
        : AttributionTypeToSpec[multiAttribution.type].label;
    } else {
      // We only want to show the placeholder text if it's open AND the user
      // doesn't have a selected attribution type already
      const isOpen = this.isMultiAttributionMenuOpen(index);
      if (isOpen) {
        return `Select value...`;
      }
      return ``;
    }
  }

  canAddMultiAttribution(index: number) {
    const row = this.getRows(true)[index];
    return (
      this.attr(`can-use-multi-attribution`) &&
      !row.isPlaceholder() &&
      !this.getMultiAttribution(index) &&
      isEventBasedType(row.entry.type)
    );
  }

  isSupportedMultiAttributionTypeForEntry(type: MultiAttributionType, index: number): boolean {
    const entry: QueryEntry = this.getEntry(index);
    return isSupportedMultiAttributionTypeForEntry(entry, type);
  }

  onOpenMultiAttributionMenu(index: number) {
    this.update({openEntry: {index, part: EntryPart.MultiAttribution}});
  }

  onMultiAttributionMenuChange(ev: MenuChangeEvent, index: number) {
    if (ev.detail.isOpen) {
      this.onOpenMultiAttributionMenu(index);
    } else {
      this.abortRowStage();
    }
  }

  onMultiAttributionChange(ev: MultiAttributionChangeEvent, index: number) {
    this.updateMultiAttribution(index, ev.detail.multiAttribution);
  }

  onMultiAttributionCancel() {
    this.abortRowStage();
  }

  onMultiAttributionDelete(index: number) {
    this.updateMultiAttribution(index, null);
    this.abortRowStage();
  }

  updateMultiAttribution(index: number, multiAttribution: MultiAttribution) {
    const entry = this.getEntry<QueryEventEntry>(index);
    const updatedEntry: QueryEventEntry = {...entry, multiAttribution};
    this.updateEntries(Action.Edit, {entry: updatedEntry, index});
  }

  onMultiAttributionLookbackMenuChange(ev: MenuChangeEvent, index: number) {
    if (ev.detail.isOpen) {
      this.update({openEntry: {index, part: EntryPart.MultiAttributionLookback}});
    } else {
      this.abortRowStage();
    }
  }

  onMultiAttributionLookbackCancel() {
    this.abortRowStage();
  }

  getMultiAttributionBehavior(index: number): Behavior | undefined {
    const row = this.getRows(true)[index];
    return isSegmentEntry(row.entry) && isMultiAttributionSegmentation(row.entry.event)
      ? row.entry.event.behavior
      : undefined;
  }

  getStagedMultiAttributionBehaviorFilter(index: number): PropertyFilter | undefined {
    const multiAttributionBehavior = this.getMultiAttributionBehavior(index);
    if (!multiAttributionBehavior) {
      return undefined;
    }

    const segmentation = multiAttributionBehavior.filters[0];
    if (segmentation) {
      return new PropertyFilter(segmentation);
    }

    const {index: activeIndex, part: activePart} = this.state.openEntry;
    if (
      activeIndex === index &&
      (activePart === EntryPart.MultiAttributionBehaviorFilterValue ||
        activePart === EntryPart.MultiAttributionBehaviorFilterOperator)
    ) {
      // Show default property filter if in the process of adding initial touchpoint filter
      return new PropertyFilter({
        ...propertySelectionToSegmentation(multiAttributionBehavior.property),
        filterOperator: StringOperator.DoesNotEqual,
        filterValue: [],
      });
    }

    return undefined;
  }

  getMultiAttributionBehaviorFilterOperatorSections({index}: {index: number}): Section[] {
    const filter = this.getStagedMultiAttributionBehaviorFilter(index);
    return [
      {
        items: [
          {value: StringOperator.DoesNotEqual, label: `Touchpoints exclude`},
          {value: StringOperator.Equals, label: `Touchpoints include`},
        ].map((item) => {
          return {
            ...item,
            isSelected: item.value === filter.filterOperator,
          };
        }),
      },
    ];
  }

  onMultiAttributionBehaviorFilterOperatorMenuChange(ev: MenuChangeEvent, index: number): void {
    if (ev.detail.isOpen) {
      this.update({openEntry: {index, part: EntryPart.MultiAttributionBehaviorFilterOperator}});
    } else {
      this.abortRowStage();
    }
  }

  openMultiAttributionBehaviorFilterValue(index: number): void {
    this.update({openEntry: {index, part: EntryPart.MultiAttributionBehaviorFilterValue}});
  }

  onMultiAttributionBehaviorFilterValueMenuChange(ev: MenuChangeEvent, index: number): void {
    if (ev.detail.isOpen) {
      this.openMultiAttributionBehaviorFilterValue(index);
    } else {
      this.abortRowStage();
    }
  }

  /**
   * Applies a first time event filter as the first filter for a given entry
   * * @param index
   */
  applyFirstTimeEventFilter(index: number) {
    const firstTimeEventFilter = createFirstTimeEventFilter();
    const entry: QuerySuperEntry = {
      ...this.getEntry(index),
      filters: [firstTimeEventFilter, ...(this.getEntry(index).filters ?? [])],
    };
    this.updateEntries(Action.Edit, {entry, index});
    this.trackAction(index, entry, Action.AddFilter, generateFilterTrackingProps(firstTimeEventFilter));
  }

  async onMapLookupTable(index: number) {
    const entry = this.getEntry(index);
    let segmentation;
    if (entry.type === SelectorTypeDefinitions.Segment) {
      segmentation = this.getSegmentationProperty(entry);
    } else {
      segmentation = entry.event;
    }
    const property = segmentationToPropertyItem(segmentation);

    const attrs: Omit<MapLookupTableModalAttrs, 'open'> = {
      'project-id': this.attr(`project-id`),
      'workspace-id': this.attr(`workspace-id`),
      'user-id': this.attr(`user-id`),
      'project-has-people': this.attr(`project-has-people`),
      'selected-property': (JSON.stringify(property) as unknown) as PropertyItemWithValue,
      'event-names': this.attr(`event-names`),
      'disable-property-selection': true,
      elref: `mapLookupTableModal`,
      type: `temporary-map`,
    };

    this.dispatchTrack(TrackingEvent.MapTableClicked, {
      ...this.getEntryTrackingProps(index),
    });

    await promisifyModal({
      tagName: `mp-map-lookup-table-modal`,
      closeEvents: [`close`],
      attrs,
    });
  }

  isComputedListPropertyEntry(row: EntryRow) {
    const property = `propertyName` in row.entry.event && row.entry.event.propertyName;

    return COMPUTED_LIST_PROPERTIES_VALUES.has(property);
  }

  isMultiAttributionSegmentationEntry(row: EntryRow) {
    if (row.entry.type !== SelectorTypeDefinitions.Segment) {
      return false;
    }
    const property = this.getSegmentationProperty(row.entry);
    return Boolean(this.getSegmentationProperty(row.entry)) && isMultiAttributionSegmentation(property);
  }

  canTypecast(row: EntryRow): boolean {
    if (this.isComputedListPropertyEntry(row)) {
      return false;
    }

    switch (row.entry.type) {
      case SelectorTypeDefinitions.Segment: {
        return Boolean(this.getSegmentationProperty(row.entry)) && !this.isMultiAttributionSegmentationEntry(row);
      }
      case SelectorTypeDefinitions.Filter: {
        const filter = row.entry.event;
        return (
          !this.helpers.isFirstTimeEventFilter(filter as PropertyFilter) && filter.resourceType !== ResourceType.Cohort
        );
      }
      default:
        return false;
    }
  }

  canMapLookupTable(row: EntryRow): boolean {
    const {entry} = row;
    if (
      row.isPlaceholder() ||
      !isPropertyEntry(entry) ||
      isObjectEntry(entry) ||
      this.attr(`disable-map-to-lookup-table`)
    ) {
      return false;
    }

    if (entry.type === SelectorTypeDefinitions.Filter) {
      const prop = transformFilterToPropertyItem(entry.event);
      if (Array.isArray(prop) || !isProperty(prop)) {
        return false;
      }
      return this.canMapPropToLookupTable(prop);
    } else if (isSegmentEntry(entry) && this.getSegmentationProperty(entry)) {
      const prop = segmentationToPropertyItem(this.getSegmentationProperty(entry));
      return this.canMapPropToLookupTable(prop);
    } else {
      return false;
    }
  }

  canMapPropToLookupTable(prop: Property): boolean {
    if (Array.isArray(prop) || Array.isArray(prop.value) || prop.behavior) {
      return false;
    }
    const custom = Boolean(prop.customPropertyId);
    const value = custom ? prop.customPropertyId : prop.value;
    if (getTempLookupTableMappings(prop.resourceType, custom).has(value)) {
      return false;
    }

    return !propIneligibleForMappingReason(prop, true);
  }

  onUnmapLookupTable(index: number) {
    const trackingProps = this.getEntryTrackingProps(index);
    const entry = this.getEntry(index);
    let segmentation;
    if (entry.type === SelectorTypeDefinitions.Segment) {
      segmentation = this.getSegmentationProperty(entry);
    } else {
      segmentation = entry.event;
    }
    const property = segmentationToPropertyItem(segmentation);
    unregisterTempLookupTableMapping(property);
    if (entry.type === SelectorTypeDefinitions.Segment) {
      this.updateEntries(Action.Remove, {index});
    }
    addToast({
      message: `Removed temporary lookup table mapping`,
      type: ToastType.DESTRUCTIVE,
    });
    this.dispatchTrack(TrackingEvent.UnmappedTable, {
      ...trackingProps,
    });
  }

  canUnmapLookupTable(row: EntryRow): boolean {
    const {entry} = row;
    if (row.isPlaceholder() || !isPropertyEntry(entry)) {
      return false;
    }

    if (isFilterEntry(entry)) {
      return this.canUnmapPropFromLookupTable(entry.event);
    } else if (isSegmentEntry(entry) && this.getSegmentationProperty(entry)) {
      const prop = this.getSegmentationProperty(entry);
      return this.canUnmapPropFromLookupTable(prop);
    } else {
      return false;
    }
  }

  canUnmapPropFromLookupTable(prop: {
    resourceType?: ResourceType;
    propertyName?: string | string[];
    customPropertyId?: number | string;
  }) {
    if (!prop.resourceType || !prop.propertyName) {
      return false;
    }

    const custom = Boolean(prop.customPropertyId);
    const name = Array.isArray(prop.propertyName) ? prop.propertyName[0] : prop.propertyName;
    const value = custom ? prop.customPropertyId : name;
    return getTempLookupTableMappings(prop.resourceType, custom).has(value);
  }

  getRowFilteringCapabilities(row: EntryRow) {
    const {entry} = row;
    const eventValue = (entry as QueryEventEntry)?.event?.value;

    const canApplyFilter =
      !row.isPlaceholder() &&
      entry.type !== ResourceType.Cohort &&
      !isPropertyEntry(entry) &&
      !this.attr(`disable-add-event-filters`);

    /** @todo [pablo] add support for 1st time filters for behaviors */
    const canApplyFirstTimeEventFilter =
      canApplyFilter &&
      !this.helpers.hasFirstTimeEventFilter(row.index) &&
      !this.attr(`disable-first-time-event-filters`) &&
      isEventBasedType(entry.type);

    // first time filters are allowed on regular events
    const firstTimeFilterDisabled =
      canApplyFirstTimeEventFilter &&
      [SPECIAL_EVENTS.SESSION_START, SPECIAL_EVENTS.SESSION_END, SPECIAL_EVENTS.TOP_EVENTS].includes(eventValue);

    return {
      canApplyFilter,
      canApplyFirstTimeEventFilter,
      firstTimeFilterDisabled,
    };
  }

  /** @todo [pablo] update cohorts QB to remove this conditional */
  public hasNewQBUI() {
    return queryBuilderUtils.isCoreReport() && this.attr(`track-name`) !== `cohorts`;
  }

  public hasNewCountMenu() {
    return (
      FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProjectStore.value) && this.hasNewQBUI()
    );
  }

  public canUseInlineBehaviors() {
    return this.hasNewQBUI() && hasInlineBehaviors(this.getContext(`currentProject`).get()?.value);
  }

  /**
   * Generates a SectionItem from a given aggregation
   *
   * @param aggregationItem
   */
  public generateAggregationSectionItem(aggregation: SelectorTypeAggregation) {
    return {
      label: this.formatDataGroupLabel(aggregation.label),
      subLabel: this.formatDataGroupLabel(aggregation.subLabel),
      value: aggregation,
      tooltip: aggregation.description
        ? {
            text: this.formatDataGroupLabel(aggregation.description),
            size: DisplaySize.Small,
          }
        : null,
    };
  }
}

export const QueryEntrySectionEl = defineAndMakeJsxFactory<QueryEntrySectionAttrsJsxProps>(
  COMPONENT_NAME,
  QueryEntrySection,
);
export const QueryEntrySectionReact = panel2react<QueryEntrySectionAttrsJsxProps>(COMPONENT_NAME, QueryEntrySection);
